import { CircleTouchProgramBuffers } from './circlePageChange';
import { ChangeTextureIndexes, ChangeTextureSet, FingerTip, TipApplication } from './fingertip';
import { WorkingItem, GPUChecker, GPURunner, ProgramBuffers, ProgramInfo, UniformLocations } from './GPURunner';
import { getLookUpTable } from './kubelkaMonk';
import { Page } from './page';
import { PageChange, PageChangeEntry } from './PageChange';
import { Point2D } from './utils';


export interface TouchUniformLocations extends UniformLocations {

    kmLookupTexNum: WebGLUniformLocation,
   
    debug: WebGLUniformLocation,
    writeAccum: WebGLUniformLocation,
    resolution: WebGLUniformLocation,
    maxDepthTextureOffset: WebGLUniformLocation,
    actionTextureOffset: WebGLUniformLocation,
    maxDepthTextureIndex: WebGLUniformLocation,
    actionTextureIndex: WebGLUniformLocation,
    referenceLatentCTexNum: WebGLUniformLocation,
    referenceLatentMTexNum: WebGLUniformLocation,
    referenceWetDepthTexNum: WebGLUniformLocation,
    referenceDryDepthTexNum: WebGLUniformLocation,
    workingLatentCTexNum: WebGLUniformLocation,
    workingLatentMTexNum: WebGLUniformLocation,
    workingWetDepthTexNum: WebGLUniformLocation,
    workingDryDepthTexNum: WebGLUniformLocation,
    accumLatentCTexNum: WebGLUniformLocation,
    accumLatentMTexNum: WebGLUniformLocation,
    accumWetDepthTexNum: WebGLUniformLocation,
    accumDryDepthTexNum: WebGLUniformLocation,
    actionFromTexNum: WebGLUniformLocation,
    actionFracTexNum: WebGLUniformLocation,
    actionCommitTexNum: WebGLUniformLocation,
    maxDepthTexNum: WebGLUniformLocation,
    dryTransmits: WebGLUniformLocation,
    doAntiAlias: WebGLUniformLocation,
    doDepthLighting: WebGLUniformLocation,
    workingWetAgeTexNum: WebGLUniformLocation,
};

export interface TouchProgramInfo extends ProgramInfo {
    uniformLocations: TouchUniformLocations
};

export interface TouchProgramBuffers extends ProgramBuffers {
    kmLookupTexture: WebGLTexture,
    nullDepthTexture: WebGLTexture | null,
    nullLatentCTexture: WebGLTexture | null,
    nullLatentMTexture: WebGLTexture | null,
};

export abstract class TouchPageChange extends PageChange {
    
    static debugRenderDepth: TouchProgramInfo = {tag:'debugRenderDepth', uniformLocations:{}} as TouchProgramInfo;

    static externalToLatentProgram: TouchProgramInfo = {tag:'externalToLatentProgram', uniformLocations:{}} as TouchProgramInfo;
    static latentToExternalProgram: TouchProgramInfo = {tag:'latentToExternalProgram', uniformLocations:{}} as TouchProgramInfo;
    static processAccumulateTakesProgram: TouchProgramInfo = {tag:'processAccumulateTakesProgram', uniformLocations:{}} as TouchProgramInfo;
    static processCommitAccumulatorProgram: TouchProgramInfo = {tag:'processCommitAccumulatorProgram', uniformLocations:{}} as TouchProgramInfo;
    static wetChangesToVisibleChangesProgram: TouchProgramInfo = {tag:'wetChangesToVisibleChanges', uniformLocations:{}} as TouchProgramInfo;
    static visibleChangesToFinalProgram: TouchProgramInfo = {tag:'visibleChangesToFinal', uniformLocations:{}} as TouchProgramInfo;
    static centralPullWetChangesProgram: TouchProgramInfo = {tag:'centralPullWetChangesProgram', uniformLocations:{}} as TouchProgramInfo;
    static debugTypeEffects: TouchProgramInfo = {tag:'debugTypeEffects', uniformLocations:{}} as TouchProgramInfo;
    
    static touchBuffers: TouchProgramBuffers | undefined;

  
    static fsColorMixLatentSource = `
#line 10096
highp uvec4 emptyLatentPart = uvec4(0, 0, 0, 0);
highp uvec4 smudgeLatentM = uvec4(0, 0, 0, 1);
highp uvec4 wetScrapeLatentM = uvec4(0, 0, 0, 2);
highp uvec4 dryScrapeLatentM = uvec4(0, 0, 0, 3);
highp uvec4 neutralLatentM = uvec4(43000, 33000, 27000, 0);
highp uvec4 whiteLatentC = uvec4(0, 0, 0, 65535);
highp uvec4 greyLatentC = uvec4(0, 0, 0, 42000);
highp uvec4 cyanLatentC = uvec4(65000, 0, 0, 32000);
highp uvec4 mustardLatentC = uvec4(0, 65000, 0, 0);
highp uvec4 greenLatentC = uvec4(65000, 65000, 0, 65535);
highp uvec4 orangeLatentC = uvec4(0, 65000, 65000, 0);
highp uvec4 redLatentC = uvec4(0, 0, 65000, 0);
highp uvec4 violetLatentC = uvec4(65000, 0, 65000, 0);

// struct Latent {
//     highp float concentration0;
//     highp float concentration1;
//     highp float concentration2;
//     highp float concentration3;
//     highp float missingRed;
//     highp float missingGreen;
//     highp float missingBlue;
//     highp float isSmudge;
// };
// replaced by array of floats because of struct precision issues

highp float emptyLatent[8] = float[](0.0,0.0,0.0,0.0,0.0,0.0,0.0,0.0);
highp float smudgeLatent[8] = float[](0.0,0.0,0.0,0.0,0.0,0.0,0.0,1.0);
`;

    static fsColorMixLerpLatentSource = TouchPageChange.fsColorMixLatentSource + `
#line 10128
highp float u_latentScale = 65535.0;
highp float u_maxC0 = 0.993;
highp float u_minC0 = 0.0;
highp float u_maxC1 = 0.991;
highp float u_minC1 = 0.0;
highp float u_maxC2 = 0.998;
highp float u_minC2 = 0.0;
highp float u_maxC3 = 1.0;
highp float u_minC3 = -0.0087;
highp float u_maxMRed = 0.19;
highp float u_minMRed = -0.31;
highp float u_maxMGreen = 0.38;
highp float u_minMGreen = -0.38;
highp float u_maxMBlue = 0.39;
highp float u_minMBlue = -0.24;

// many latents give the same rgb.
bool latentPartMatch(uvec4 cpart1, uvec4 cpart2, uvec4 mpart1, uvec4 mpart2) {
    if (mpart1.a != mpart2.a) {
        return false;
    }
    if (abs(float(cpart1.r) - float(cpart2.r)) > 60.0 || abs(float(cpart1.g) - float(cpart2.g)) > 60.0 || abs(float(cpart1.b) - float(cpart2.b)) > 60.0 || abs(float(cpart1.a) - float(cpart2.a)) > 60.0 ||
        abs(float(mpart1.r) - float(mpart2.r)) > 60.0 || abs(float(mpart1.g) - float(mpart2.g)) > 60.0 || abs(float(mpart1.b) - float(mpart2.b)) > 60.0 || abs(float(mpart1.a) - float(mpart2.a)) > 60.0) {
        return false;
    }
    return true;
}

bool latentsMatch(highp float latent1[8], highp float latent2[8]) {
    return (latent1[7] > 0.0 && latent2[7] > 0.0) || 
        (latent1[0] == latent2[0] && latent1[1] == latent2[1] && latent1[2] == latent2[2] && latent1[3] == latent2[3] && 
        latent1[4] == latent2[4] && latent1[5] == latent2[5] && latent1[6] == latent2[6] && latent1[7] == latent2[7]);
}

highp uvec4 latentToLatentC(highp float latent[8]) {
    if (latent[7] > 0.0 || latentsMatch(latent, emptyLatent)) {
        return emptyLatentPart;
    }
    highp float c0 = clamp(latent[0], u_minC0, u_maxC0);
    highp float c1 = clamp(latent[1], u_minC1, u_maxC1);
    highp float c2 = clamp(latent[2], u_minC2, u_maxC2);
    highp float c3 = clamp(latent[3], u_minC3, u_maxC3);
    c0 = (c0 - u_minC0) / (u_maxC0 - u_minC0);
    c1 = (c1 - u_minC1) / (u_maxC1 - u_minC1);
    c2 = (c2 - u_minC2) / (u_maxC2 - u_minC2);
    c3 = (c3 - u_minC3) / (u_maxC3 - u_minC3);
    return uvec4(uint((c0 * u_latentScale) + 0.5), uint((c1 * u_latentScale) + 0.5), uint((c2 * u_latentScale) + 0.5), uint((c3 * u_latentScale) + 0.5));
}
highp uvec4 latentToLatentM(highp float latent[8]) {
    if (latentsMatch(latent, emptyLatent)) {
        return emptyLatentPart;
    }
    if (latent[7] > 0.0) {
        return smudgeLatentM;
    }

    highp float missingRed = clamp(latent[4], u_minMRed, u_maxMRed);
    highp float missingGreen = clamp(latent[5], u_minMGreen, u_maxMGreen);
    highp float missingBlue = clamp(latent[6], u_minMBlue, u_maxMBlue);
    missingRed = (missingRed - u_minMRed) / (u_maxMRed - u_minMRed);
    missingGreen = (missingGreen - u_minMGreen) / (u_maxMGreen - u_minMGreen);
    missingBlue = (missingBlue - u_minMBlue) / (u_maxMBlue - u_minMBlue);
    return uvec4(uint(round(missingRed * u_latentScale)), uint(round(missingGreen * u_latentScale)), uint(round(missingBlue * u_latentScale)), latent[7] > 0.0 ? 1u : 0u);
}
void latentFromLatentCM(highp uvec4 latentC, highp uvec4 latentM, inout highp float result[8]) {
    if (latentC == emptyLatentPart && latentM == emptyLatentPart) {
        result = emptyLatent;
        return;
    }
    if (latentC == emptyLatentPart && latentM == smudgeLatentM) {
        result = smudgeLatent;
        return;
    }
    highp float c0 = float(latentC.r) / u_latentScale * (u_maxC0 - u_minC0) + u_minC0;
    highp float c1 = float(latentC.g) / u_latentScale * (u_maxC1 - u_minC1) + u_minC1;
    highp float c2 = float(latentC.b) / u_latentScale * (u_maxC2 - u_minC2) + u_minC2;
    highp float c3 = float(latentC.a) / u_latentScale * (u_maxC3 - u_minC3) + u_minC3;
    highp float missingRed = float(latentM.r) / u_latentScale * (u_maxMRed - u_minMRed) + u_minMRed;
    highp float missingGreen = float(latentM.g) / u_latentScale * (u_maxMGreen - u_minMGreen) + u_minMGreen;
    highp float missingBlue = float(latentM.b) / u_latentScale * (u_maxMBlue - u_minMBlue) + u_minMBlue;
    highp float isSmudge = latentM.a > 0u ? 1.0 : 0.0;
    highp float _result[8];
    _result[0] = c0;
    _result[1] = c1;
    _result[2] = c2;
    _result[3] = c3;
    _result[4] = missingRed;
    _result[5] = missingGreen;
    _result[6] = missingBlue;
    _result[7] = isSmudge;

    result = _result;
}

void lerpLatent(highp float latent1[8], highp float latent2[8], highp float weight2, inout highp float result[8]) {
    if (latent1[7] > 0.0) {
        result = latent2;
        return;
    }
    if (latent2[7] > 0.0) {
        result = latent1;
        return;
    }
    highp float weight1 = 1.0 - weight2;
    highp float _result[8];
    _result[0] = latent1[0] * weight1 + latent2[0] * weight2;
    _result[1] = latent1[1] * weight1 + latent2[1] * weight2;
    _result[2] = latent1[2] * weight1 + latent2[2] * weight2;
    _result[3] = latent1[3] * weight1 + latent2[3] * weight2;
    _result[4] = latent1[4] * weight1 + latent2[4] * weight2;
    _result[5] = latent1[5] * weight1 + latent2[5] * weight2;
    _result[6] = latent1[6] * weight1 + latent2[6] * weight2;
    _result[7] = 0.0;
    result = _result;
}`;

static fsColorMixRGBToLatentSource = TouchPageChange.fsColorMixLerpLatentSource + `
#line 10223
void rgbaToLatent(highp vec4 rgba, inout highp float result[8]) {
    if (rgba.r == 1.0/255.0 && rgba.g == 1.0/255.0 && rgba.b == 1.0/255.0 && rgba.a == 1.0) {
        result = smudgeLatent;
        return;
    }
    if (rgba.r == 0.0 && rgba.g == 0.0 && rgba.b == 0.0 && rgba.a == 0.0) {
        result = emptyLatent;
        return;
    }
    highp float x = (rgba.r * 63.0);
    highp float y = (rgba.g * 63.0);
    highp float z = (rgba.b * 63.0);

    highp int ix = int(x);
    highp int iy = int(y);
    highp int iz = int(z);

    highp float tx = x - float(ix);
    highp float ty = y - float(iy);
    highp float tz = z - float(iz);

    highp int xyz = ix + iy*64 + iz*64*64;

    highp float c0 = 0.0;
    highp float c1 = 0.0;
    highp float c2 = 0.0;
    highp float w = 0.0;

    w = (1.0-tx)*(1.0-ty)*(1.0-tz); 
    c0 += w*float(texelFetch(u_kmLookupTexNum, ivec2((xyz+ 192) % 1024, (xyz+ 192) / 1024), 0).r); 
    c1 += w*float(texelFetch(u_kmLookupTexNum, ivec2((xyz+ 262336) % 1024, (xyz+ 262336) / 1024), 0).r);
    c2 += w*float(texelFetch(u_kmLookupTexNum, ivec2((xyz+ 524480) % 1024, (xyz+ 524480) / 1024), 0).r);
    w = (    tx)*(1.0-ty)*(1.0-tz); 
    c0 += w*float(texelFetch(u_kmLookupTexNum, ivec2((xyz+ 193) % 1024, (xyz+ 193) / 1024), 0).r);
    c1 += w*float(texelFetch(u_kmLookupTexNum, ivec2((xyz+ 262337) % 1024, (xyz+ 262337) / 1024), 0).r);
    c2 += w*float(texelFetch(u_kmLookupTexNum, ivec2((xyz+ 524481) % 1024, (xyz+ 524481) / 1024), 0).r);
    w = (1.0-tx)*(    ty)*(1.0-tz); 
    c0 += w*float(texelFetch(u_kmLookupTexNum, ivec2((xyz+ 256) % 1024, (xyz+ 256) / 1024), 0).r);
    c1 += w*float(texelFetch(u_kmLookupTexNum, ivec2((xyz+ 262400) % 1024, (xyz+ 262400) / 1024), 0).r);
    c2 += w*float(texelFetch(u_kmLookupTexNum, ivec2((xyz+ 524544) % 1024, (xyz+ 524544) / 1024), 0).r);
    w = (    tx)*(    ty)*(1.0-tz); 
    c0 += w*float(texelFetch(u_kmLookupTexNum, ivec2((xyz+ 257) % 1024, (xyz+ 257) / 1024), 0).r);
    c1 += w*float(texelFetch(u_kmLookupTexNum, ivec2((xyz+ 262401) % 1024, (xyz+ 262401) / 1024), 0).r);
    c2 += w*float(texelFetch(u_kmLookupTexNum, ivec2((xyz+ 524545) % 1024, (xyz+ 524545) / 1024), 0).r);
    w = (1.0-tx)*(1.0-ty)*(    tz); 
    c0 += w*float(texelFetch(u_kmLookupTexNum, ivec2((xyz+ 4288) % 1024, (xyz+ 4288) / 1024), 0).r);
    c1 += w*float(texelFetch(u_kmLookupTexNum, ivec2((xyz+ 266432) % 1024, (xyz+ 266432) / 1024), 0).r);
    c2 += w*float(texelFetch(u_kmLookupTexNum, ivec2((xyz+ 528576) % 1024, (xyz+ 528576) / 1024), 0).r);
    w = (    tx)*(1.0-ty)*(    tz); 
    c0 += w*float(texelFetch(u_kmLookupTexNum, ivec2((xyz+ 4289) % 1024, (xyz+ 4289) / 1024), 0).r);
    c1 += w*float(texelFetch(u_kmLookupTexNum, ivec2((xyz+ 266433) % 1024, (xyz+ 266433) / 1024), 0).r);
    c2 += w*float(texelFetch(u_kmLookupTexNum, ivec2((xyz+ 528577) % 1024, (xyz+ 528577) / 1024), 0).r);
    w = (1.0-tx)*(    ty)*(    tz); 
    c0 += w*float(texelFetch(u_kmLookupTexNum, ivec2((xyz+ 4352) % 1024, (xyz+ 4352) / 1024), 0).r);
    c1 += w*float(texelFetch(u_kmLookupTexNum, ivec2((xyz+ 266496) % 1024, (xyz+ 266496) / 1024), 0).r);
    c2 += w*float(texelFetch(u_kmLookupTexNum, ivec2((xyz+ 528640) % 1024, (xyz+ 528640) / 1024), 0).r);
    w = (    tx)*(    ty)*(    tz); 
    c0 += w*float(texelFetch(u_kmLookupTexNum, ivec2((xyz+ 4353) % 1024, (xyz+ 4353) / 1024), 0).r);
    c1 += w*float(texelFetch(u_kmLookupTexNum, ivec2((xyz+ 266497) % 1024, (xyz+ 266497) / 1024), 0).r);
    c2 += w*float(texelFetch(u_kmLookupTexNum, ivec2((xyz+ 528641) % 1024, (xyz+ 528641) / 1024), 0).r);

    c0 /= 255.0;
    c1 /= 255.0;
    c2 /= 255.0;

    highp float c3 = 1.0 - (c0 + c1 + c2);

    highp float c00 = c0 * c0;
    highp float c11 = c1 * c1;
    highp float c22 = c2 * c2;
    highp float c33 = c3 * c3;
    highp float c01 = c0 * c1;
    highp float c02 = c0 * c2;
    highp float c12 = c1 * c2;

    highp float rmix = 0.0;
    highp float gmix = 0.0;
    highp float bmix = 0.0;
    
    w = 0.0;

    w = c0*c00; rmix += +0.07717053*w; gmix += +0.02826978*w; bmix += +0.24832992*w;
    w = c1*c11; rmix += +0.95912302*w; gmix += +0.80256528*w; bmix += +0.03561839*w;
    w = c2*c22; rmix += +0.74683774*w; gmix += +0.04868586*w; bmix += +0.00000000*w;
    w = c3*c33; rmix += +0.99518138*w; gmix += +0.99978149*w; bmix += +0.99704802*w;
    w = c00*c1; rmix += +0.04819146*w; gmix += +0.83363781*w; bmix += +0.32515377*w;
    w = c01*c1; rmix += -0.68146950*w; gmix += +1.46107803*w; bmix += +1.06980936*w;
    w = c00*c2; rmix += +0.27058419*w; gmix += -0.15324870*w; bmix += +1.98735057*w;
    w = c02*c2; rmix += +0.80478189*w; gmix += +0.67093710*w; bmix += +0.18424500*w;
    w = c00*c3; rmix += -0.35031003*w; gmix += +1.37855826*w; bmix += +3.68865000*w;
    w = c0*c33; rmix += +1.05128046*w; gmix += +1.97815239*w; bmix += +2.82989073*w;
    w = c11*c2; rmix += +3.21607125*w; gmix += +0.81270228*w; bmix += +1.03384539*w;
    w = c1*c22; rmix += +2.78893374*w; gmix += +0.41565549*w; bmix += -0.04487295*w;
    w = c11*c3; rmix += +3.02162577*w; gmix += +2.55374103*w; bmix += +0.32766114*w;
    w = c1*c33; rmix += +2.95124691*w; gmix += +2.81201112*w; bmix += +1.17578442*w;
    w = c22*c3; rmix += +2.82677043*w; gmix += +0.79933038*w; bmix += +1.81715262*w;
    w = c2*c33; rmix += +2.99691099*w; gmix += +1.22593053*w; bmix += +1.80653661*w;
    w = c01*c2; rmix += +1.87394106*w; gmix += +2.05027182*w; bmix += -0.29835996*w;
    w = c01*c3; rmix += +2.56609566*w; gmix += +7.03428198*w; bmix += +0.62575374*w;
    w = c02*c3; rmix += +4.08329484*w; gmix += -1.40408358*w; bmix += +2.14995522*w;
    w = c12*c3; rmix += +6.00078678*w; gmix += +2.55552042*w; bmix += +1.90739502*w;

    highp float _result[8];
    _result[0] = c0;
    _result[1] = c1;
    _result[2] = c2;
    _result[3] = c3;
    _result[4] = rgba.r-rmix;
    _result[5] = rgba.g-gmix;
    _result[6] = rgba.b-bmix;
    _result[7] = 0.0;
    result = _result;
}
`;

static fsColorMixLatentToRGBSource = TouchPageChange.fsColorMixLerpLatentSource + `
#line 10329
highp vec3 evalPolynomial(highp float c0, highp float c1, highp float c2, highp float c3) {
    highp float r = 0.0;
    highp float g = 0.0;
    highp float b = 0.0;

    highp float c00 = c0 * c0;
    highp float c11 = c1 * c1;
    highp float c22 = c2 * c2;
    highp float c33 = c3 * c3;
    highp float c01 = c0 * c1;
    highp float c02 = c0 * c2;
    highp float c12 = c1 * c2;

    highp float w = 0.0;
    w = c0*c00; r += +0.07717053*w; g += +0.02826978*w; b += +0.24832992*w;
    w = c1*c11; r += +0.95912302*w; g += +0.80256528*w; b += +0.03561839*w;
    w = c2*c22; r += +0.74683774*w; g += +0.04868586*w; b += +0.00000000*w;
    w = c3*c33; r += +0.99518138*w; g += +0.99978149*w; b += +0.99704802*w;
    w = c00*c1; r += +0.04819146*w; g += +0.83363781*w; b += +0.32515377*w;
    w = c01*c1; r += -0.68146950*w; g += +1.46107803*w; b += +1.06980936*w;
    w = c00*c2; r += +0.27058419*w; g += -0.15324870*w; b += +1.98735057*w;
    w = c02*c2; r += +0.80478189*w; g += +0.67093710*w; b += +0.18424500*w;
    w = c00*c3; r += -0.35031003*w; g += +1.37855826*w; b += +3.68865000*w;
    w = c0*c33; r += +1.05128046*w; g += +1.97815239*w; b += +2.82989073*w;
    w = c11*c2; r += +3.21607125*w; g += +0.81270228*w; b += +1.03384539*w;
    w = c1*c22; r += +2.78893374*w; g += +0.41565549*w; b += -0.04487295*w;
    w = c11*c3; r += +3.02162577*w; g += +2.55374103*w; b += +0.32766114*w;
    w = c1*c33; r += +2.95124691*w; g += +2.81201112*w; b += +1.17578442*w;
    w = c22*c3; r += +2.82677043*w; g += +0.79933038*w; b += +1.81715262*w;
    w = c2*c33; r += +2.99691099*w; g += +1.22593053*w; b += +1.80653661*w;
    w = c01*c2; r += +1.87394106*w; g += +2.05027182*w; b += -0.29835996*w;
    w = c01*c3; r += +2.56609566*w; g += +7.03428198*w; b += +0.62575374*w;
    w = c02*c3; r += +4.08329484*w; g += -1.40408358*w; b += +2.14995522*w;
    w = c12*c3; r += +6.00078678*w; g += +2.55552042*w; b += +1.90739502*w;
    
    return vec3(r, g, b);
}

highp vec4 latentToRgba(highp float latent[8]) {
    if (latent[7] > 0.0) {
        return vec4(1.0/255.0, 1.0/255.0, 1.0/255.0, 1.0);
    }
    if (latentsMatch(latent, emptyLatent)) {
        return vec4(0.0, 0.0, 0.0, 0.0);
    }

    highp vec3 rgb = evalPolynomial(latent[0], latent[1], latent[2], latent[3]);

    highp float  r = max(0.0, min(1.0, rgb.r + latent[4]));
    highp float  g = max(0.0, min(1.0, rgb.g + latent[5]));
    highp float  b = max(0.0, min(1.0, rgb.b + latent[6]));

    if (r < 1.6/255.0 && g < 1.6/255.0 && b < 1.6/255.0) {
        r=g=b=0.0;
    }
    return vec4(r, g, b, 1.0);
}
`;

static fsActionPositionsSource = `
#line 10390

highp ivec3 workingPositionToActionPosition(highp ivec2 workingPosition, highp uvec2 workingSize, highp ivec2 actionOffset, highp uvec2 actionSize, highp int textureIndex) {
    // external texture so flip y
    workingPosition.y = int(workingSize.y) - workingPosition.y - 1;

    highp ivec3 actionPosition = ivec3(workingPosition - actionOffset, textureIndex);
    if (actionPosition.x < 0 || uint(actionPosition.x) >= actionSize.x || actionPosition.y < 0 || uint(actionPosition.y) >= actionSize.y) {
        actionPosition.x = -1;
        actionPosition.y = -1;
    } 
    return actionPosition;
}

highp ivec2 actionIndexToWorkingPosition(highp uint actionIndex, highp ivec2 actionOffset, highp uvec2 actionSize, highp uvec2 workingSize) {
    highp ivec2 actionPosition = ivec2(actionIndex % actionSize.x, actionIndex / actionSize.x);
    highp ivec2 workingPosition = actionPosition + actionOffset;
    // external texture so flip y
    workingPosition.y = int(workingSize.y) - workingPosition.y - 1;
    return workingPosition;
}
`;


static fsSourceExternalToLatent = `#version 300 es
#line 10416
precision highp float;
precision highp int;
//precision highp uint;
uniform highp usampler2D u_kmLookupTexNum;
uniform highp sampler2D u_externalColorTexNum;
uniform highp usampler2D u_externalDepthTexNum;
uniform highp vec2 u_resolution;
in highp vec2 v_texCoordRendered;
in highp vec2 v_texCoordExternal;
layout(location = 0) out highp uvec4 outputLatentC;
layout(location = 1) out highp uvec4 outputLatentM;
layout(location = 2) out highp uint outputDepth;
` + TouchPageChange.fsColorMixRGBToLatentSource + `
#line 10426
void main() {
    outputDepth = texture(u_externalDepthTexNum, v_texCoordExternal).r;
    highp vec4 outputColor = texture(u_externalColorTexNum, v_texCoordExternal);
    highp float outputLatent[8];
    rgbaToLatent(outputColor, outputLatent);
    outputLatentC = latentToLatentC(outputLatent);
    outputLatentM = latentToLatentM(outputLatent);
    latentFromLatentCM(outputLatentC, outputLatentM, outputLatent);

}`;

static fsSourceDoDepthLighting = `
#line 10436
highp vec4 doDepthLighting(highp vec4 outputColor, highp uint depthHere, highp usampler2D wetDepthTexNum, highp usampler2D dryDepthTexNum, highp vec2 texCoordRendered) {
    highp uvec2 workingSize = uvec2(textureSize(wetDepthTexNum, 0));
    highp ivec2 workingPos = ivec2(texCoordRendered * vec2(workingSize));
    if (workingPos.x < int(workingSize.x - 1u) && workingPos.y < int(workingSize.y - 1u)) {
        depthHere += texelFetch(dryDepthTexNum, workingPos, 0).r;
        highp ivec2 posThere = ivec2(workingPos.x+1, workingPos.y+1);
        highp uint depthThere = texelFetch(wetDepthTexNum, posThere, 0).r;
        depthThere += texelFetch(dryDepthTexNum, posThere, 0).r;
        depthHere = min(depthHere, 3000u);
        depthThere = min(depthThere, 3000u);

        if (depthHere != depthThere) {
            // convert into normalized cube root space, since volume grows with the cube of height.
            highp float depthHereAlt = pow(float(depthHere), 1.0/3.0) / pow(3000.0, 1.0/3.0);
            highp float depthThereAlt = pow(float(depthThere), 1.0/3.0) / pow(3000.0, 1.0/3.0);
            // in one x move the height changes by the delta
            highp vec2 depthDiffVec = normalize(vec2(-1.0, depthHereAlt - depthThereAlt));
            // normal vector of that paint slope face that's pointing into the light source
            highp vec2 depthDiffNormalVec = vec2(depthDiffVec.y, -depthDiffVec.x);
            // cosine of the light source (up, right) angle and the paint slope face is the amount of incident light
            highp float align = dot(depthDiffNormalVec, normalize(vec2(1.0, 1.0)));
            // i want flat paint to get the 'expected' color but that is at a 45 to the light, so adapt
            align = align - .7071068;
            // how bright (dim actually) is the color, because this effect is much harder to see on darker paint, so boost it
            highp float lum = 1.0 - (0.299*outputColor.r + 0.587*outputColor.g + 0.114*outputColor.b);
            // apply the effect using magic numbers. the 0.13 is what makes white paint show a bit of the effect and the 0.60 is the boost that makes black paint show a small effect.
            outputColor.rgb += align * (0.13 + 0.60 * lum);
            outputColor.rgb = max(min(outputColor.rgb, vec3(1.0,1.0,1.0)), vec3(0.0,0.0,0.0));

        }
    }
    return outputColor;
}

`;

static fsSourceLatentToExternal = `#version 300 es
#line 10478
precision highp float;
precision highp int;
//precision highp uint;
uniform highp usampler2D u_workingLatentCTexNum;
uniform highp usampler2D u_workingLatentMTexNum;
uniform highp usampler2D u_workingWetDepthTexNum;
uniform highp usampler2D u_workingDryDepthTexNum;
uniform highp usampler2D u_kmLookupTexNum;
uniform highp int u_doDepthLighting;
in highp vec2 v_texCoordRendered;
in highp vec2 v_texCoordExternal;
layout(location = 0) out highp vec4 outputColor;
layout(location = 1) out highp uint outputDepth;
` + TouchPageChange.fsColorMixLatentToRGBSource  
 + TouchPageChange.fsSourceDoDepthLighting
+ `
#line 10488
void main() {
    outputDepth = texture(u_workingWetDepthTexNum, v_texCoordRendered).r;
    highp uvec4 latentC = texture(u_workingLatentCTexNum, v_texCoordRendered);
    highp uvec4 latentM = texture(u_workingLatentMTexNum, v_texCoordRendered);
    highp float outputLatent[8];
    latentFromLatentCM(latentC, latentM, outputLatent);
    outputColor = latentToRgba(outputLatent);
    if (u_doDepthLighting > 0) {
        outputColor = doDepthLighting(outputColor, outputDepth, u_workingWetDepthTexNum, u_workingDryDepthTexNum, v_texCoordRendered);
    }
}`;

static fsSourceProcessGlobals = `#version 300 es
#line 10508
precision highp float;
precision highp int;
//precision highp uint;
uniform highp usampler2D u_workingLatentCTexNum;
uniform highp usampler2D u_workingLatentMTexNum;
uniform highp usampler2D u_workingWetDepthTexNum;
uniform highp usampler2D u_workingDryDepthTexNum;
uniform highp usampler2D u_accumLatentCTexNum;
uniform highp usampler2D u_accumLatentMTexNum;
uniform highp usampler2D u_accumWetDepthTexNum;
uniform highp usampler2D u_accumDryDepthTexNum;
uniform highp usampler2D u_referenceLatentCTexNum;
uniform highp usampler2D u_referenceLatentMTexNum;
uniform highp usampler2D u_referenceWetDepthTexNum;
uniform highp usampler2D u_referenceDryDepthTexNum;
uniform highp usampler2DArray u_maxDepthTexNum;
uniform highp sampler2DArray u_actionCommitTexNum;
uniform highp usampler2DArray u_actionFromTexNum;
uniform highp sampler2DArray u_actionFracTexNum;
uniform highp int u_maxDepthTextureIndex;
uniform highp int u_actionTextureIndex;
uniform highp int u_writeAccum;
uniform highp int u_debug;
in highp vec2 v_texCoordRendered;
in highp vec2 v_texCoordExternal;
layout(location = 0) out highp uvec4 outputLatentC;
layout(location = 1) out highp uvec4 outputLatentM;
layout(location = 2) out highp uint outputDepth;
uniform highp ivec2 u_actionTextureOffset;
uniform highp ivec2 u_maxDepthTextureOffset;
`;

static fsSourceProcessAccumulateTakes =
    TouchPageChange.fsSourceProcessGlobals  
    + TouchPageChange.fsColorMixLerpLatentSource
    + TouchPageChange.fsActionPositionsSource + 
`
#line 10536

highp float accumulateTakes(highp float error, highp uint srcOffset, highp float srcFrac, highp ivec2 actionTextureOffset, highp uvec2 actionTextureSize, 
    highp ivec2 maxTextureOffset, highp uvec2 maxTextureSize, highp int maxDepthTextureIndex, highp uvec2 workingSize, 
    inout highp float outputLatent[8], inout highp uint outputDepth) {
    
    highp ivec2 workingSrcPos = actionIndexToWorkingPosition(srcOffset, actionTextureOffset, actionTextureSize, workingSize);
    highp ivec3 workingMaxPos = workingPositionToActionPosition(workingSrcPos, workingSize, maxTextureOffset, maxTextureSize, maxDepthTextureIndex);
    
    highp uint srcDepth = texelFetch(u_workingWetDepthTexNum, workingSrcPos, 0).r;
    highp uint srcMax = texelFetch(u_maxDepthTexNum, workingMaxPos, 0).r;

    highp float newError = error;
    highp float takef = ((float(srcDepth) - float(srcMax)) * srcFrac) + error;
    if (takef > 0.0) {
        highp uint takeDepth = uint(takef);
        newError = takef - float(takeDepth);
        highp float blendWeight = takef / (takef + float(outputDepth));
        highp float srcLatent[8];
        latentFromLatentCM(texelFetch(u_workingLatentCTexNum, workingSrcPos, 0), texelFetch(u_workingLatentMTexNum, workingSrcPos, 0), srcLatent);
        lerpLatent(outputLatent, srcLatent, blendWeight, outputLatent);
        outputDepth += takeDepth;
        // if (outputDepth > 65535u) {
        //     outputLatent = latentFromLatentCM(cyanLatentC, neutralLatentM);
        // }
    }
    return newError;
}

void main() {
    
    highp uvec2 workingSize = uvec2(textureSize(u_workingLatentCTexNum, 0));
    highp ivec2 workingPos = ivec2(v_texCoordRendered * vec2(workingSize));
    highp uvec2 actionSize = uvec2(textureSize(u_actionFromTexNum, 0));
    highp ivec3 actionPos = workingPositionToActionPosition(workingPos, workingSize, u_actionTextureOffset, actionSize, u_actionTextureIndex);
    highp uvec2 maxDepthSize = uvec2(textureSize(u_maxDepthTexNum, 0));
    
    outputDepth = 0u;
    outputLatentC = emptyLatentPart;
    outputLatentM = smudgeLatentM;

    if (actionPos.x < 0) {
        return;
    }
    // apply the movements
    // movement instructions are in the movements texture.
    highp uvec4 vMovements = texelFetch(u_actionFromTexNum, actionPos, 0);
    highp vec4 vFractions = texelFetch(u_actionFracTexNum, actionPos, 0);
    highp float outputLatent[8] = smudgeLatent;
    outputDepth = uint(0);

    highp float error = 0.0;
    if (vMovements.r > uint(0)) {
        error = accumulateTakes(error, vMovements.r, vFractions.r, u_actionTextureOffset, actionSize, u_maxDepthTextureOffset, maxDepthSize, u_maxDepthTextureIndex, workingSize, outputLatent, outputDepth);
    }
    if (vMovements.g > uint(0)) {
        error = accumulateTakes(error, vMovements.g, vFractions.g, u_actionTextureOffset, actionSize, u_maxDepthTextureOffset, maxDepthSize, u_maxDepthTextureIndex, workingSize, outputLatent, outputDepth);
    }
    if (vMovements.b > uint(0)) {
        error = accumulateTakes(error, vMovements.b, vFractions.b, u_actionTextureOffset, actionSize, u_maxDepthTextureOffset, maxDepthSize, u_maxDepthTextureIndex, workingSize, outputLatent, outputDepth);
    }
    if (vMovements.a > uint(0)) {
        error = accumulateTakes(error, vMovements.a, vFractions.a, u_actionTextureOffset, actionSize, u_maxDepthTextureOffset, maxDepthSize, u_maxDepthTextureIndex, workingSize, outputLatent, outputDepth);
    }
    outputLatentC = latentToLatentC(outputLatent);
    outputLatentM = latentToLatentM(outputLatent);        
    
    return;
}
`;
static fsSourceProcessCommitAccumulator = 
    TouchPageChange.fsSourceProcessGlobals  
    + TouchPageChange.fsColorMixLerpLatentSource  
    + TouchPageChange.fsActionPositionsSource + 
`
#line 10631

void main() {

    // apply the commit texture. the accumulator adds to the source and removes down to the max depth
    highp uvec2 workingSize = uvec2(textureSize(u_workingLatentCTexNum, 0));
    highp ivec2 workingPos = ivec2(v_texCoordRendered * vec2(workingSize));
    highp uvec2 actionSize = uvec2(textureSize(u_actionFromTexNum, 0));
    highp ivec3 actionPos = workingPositionToActionPosition(workingPos, workingSize, u_actionTextureOffset, actionSize, u_actionTextureIndex);
    highp uvec2 maxDepthSize = uvec2(textureSize(u_maxDepthTexNum, 0));
    highp ivec3 maxDepthPos = workingPositionToActionPosition(workingPos, workingSize, u_maxDepthTextureOffset, maxDepthSize, u_maxDepthTextureIndex);
    
    outputDepth = texture(u_workingWetDepthTexNum, v_texCoordRendered).r;
    if (outputDepth == 0u) {
        outputLatentC = emptyLatentPart;
        outputLatentM = smudgeLatentM;
    } else {
        outputLatentC = texture(u_workingLatentCTexNum, v_texCoordRendered);
        outputLatentM = texture(u_workingLatentMTexNum, v_texCoordRendered);
    }

        if (u_debug == 1) {
        outputDepth = 13u;
        return;
    }

    if (actionPos.x < 0) {
        return;
    }
    

    highp float destCommitFrac = texelFetch(u_actionCommitTexNum, actionPos, 0).r;
    if (destCommitFrac > 0.0) {
        highp uint destMaxDepth = texelFetch(u_maxDepthTexNum, maxDepthPos, 0).r;

        if (outputDepth > destMaxDepth) {
            highp float removef = (destCommitFrac * float(outputDepth - destMaxDepth));
            highp uint remove = uint(removef);
            if (remove >= outputDepth) {
                outputDepth = uint(0);
                outputLatentC = emptyLatentPart;
                outputLatentM = smudgeLatentM;
            } else {
                outputDepth -= remove;
            }
        }
    }

    highp uint accumDepth = texture(u_accumWetDepthTexNum, v_texCoordRendered).r;
    if (u_writeAccum == 1 && accumDepth > uint(0)) {
        // blend
        highp float blendWeight = float(accumDepth) / (float(accumDepth) + float(outputDepth));
        highp float outputLatent[8];
        latentFromLatentCM(outputLatentC, outputLatentM, outputLatent);
        highp float accumLatent[8];
        latentFromLatentCM(texture(u_accumLatentCTexNum, v_texCoordRendered), texture(u_accumLatentMTexNum, v_texCoordRendered), accumLatent);
        lerpLatent(outputLatent, accumLatent, blendWeight, outputLatent);
        outputLatentC = latentToLatentC(outputLatent);
        outputLatentM = latentToLatentM(outputLatent);
        outputDepth += accumDepth;
        // if (outputDepth > 65535u) {
        //     outputLatentC = cyanLatentC;
        //     outputLatentM = neutralLatentM;
        // }
    }
}
`;

static fsSourceCentralPullWetChanges = 
TouchPageChange.fsSourceProcessGlobals   
+ TouchPageChange.fsColorMixLerpLatentSource  
+`
#line 10679

void main() {
    // working is the wet paint. reference is the starting wet. accumulator is the pull gradients
    highp uvec4 newWetLatentC = texture(u_workingLatentCTexNum, v_texCoordRendered);
    highp uvec4 newWetLatentM = texture(u_workingLatentMTexNum, v_texCoordRendered);
    highp uvec4 newWetDepth = texture(u_workingWetDepthTexNum, v_texCoordRendered);
    highp uvec4 startWetDepth = texture(u_referenceWetDepthTexNum, v_texCoordRendered);
    highp uvec4 pullParams0123 = texture(u_accumLatentCTexNum, v_texCoordRendered);
    highp uvec4 pullParams5678 = texture(u_accumLatentMTexNum, v_texCoordRendered);
    highp uvec4 pullParamsSelf = texture(u_accumWetDepthTexNum, v_texCoordRendered);

    outputDepth = newWetDepth.r;
    outputLatentC = emptyLatentPart;
    outputLatentM = emptyLatentPart;

    // first, remove percentage that gets shoved inward
    if (outputDepth > 0u) {
        highp float pctRemove = float(pullParamsSelf.r) / 60000.0;
        outputDepth -= uint(float(outputDepth) * pctRemove + 0.5);
    }
    if (outputDepth > 0u) {
        outputLatentC = newWetLatentC;
        outputLatentM = newWetLatentM;
    }

    // now, add the pulls
    highp uvec2 workingSize = uvec2(textureSize(u_accumLatentCTexNum, 0));
    highp ivec2 workingPos = ivec2(v_texCoordRendered * vec2(workingSize));
    highp float accumDepth = float(outputDepth);
    highp float outputLatent[8];
    latentFromLatentCM(outputLatentC, outputLatentM, outputLatent);

    highp float pctThere = float(pullParams0123.r) / 60000.0;
    if (pctThere > 0.0) {
        highp ivec2 posThere = ivec2(workingPos.x-1, workingPos.y+1);
        highp uint depthThere = texelFetch(u_workingWetDepthTexNum, posThere, 0).r;
        highp float giveFracThere = float(texelFetch(u_accumWetDepthTexNum, posThere, 0).r) / 60000.0;
        highp float takeAmount = float(depthThere) * giveFracThere * pctThere;
        if (takeAmount > 0.0) {
            highp uvec4 latentCThere = texelFetch(u_workingLatentCTexNum, posThere, 0);
            highp uvec4 latentMThere = texelFetch(u_workingLatentMTexNum, posThere, 0);
            highp float latentThere[8];
            latentFromLatentCM(latentCThere, latentMThere, latentThere);
            takeAmount = max(1.0, takeAmount);
            highp float blendWeight = takeAmount / (accumDepth + takeAmount);
            lerpLatent(outputLatent, latentThere, blendWeight, outputLatent);
            accumDepth += takeAmount;
        }
    }
    pctThere = float(pullParams0123.g) / 60000.0;
    if (pctThere > 0.0) {
        highp ivec2 posThere = ivec2(workingPos.x, workingPos.y+1);;
        highp uint depthThere = texelFetch(u_workingWetDepthTexNum, posThere, 0).r;
        highp float giveFracThere = float(texelFetch(u_accumWetDepthTexNum, posThere, 0).r) / 60000.0;
        highp float takeAmount = float(depthThere) * giveFracThere * pctThere;
        //outputDepth = uint(60000.0 * giveFracThere);
        //return;
        if (takeAmount > 0.0) {
            highp uvec4 latentCThere = texelFetch(u_workingLatentCTexNum, posThere, 0);
            highp uvec4 latentMThere = texelFetch(u_workingLatentMTexNum, posThere, 0);
            highp float latentThere[8];
            latentFromLatentCM(latentCThere, latentMThere, latentThere);
            takeAmount = max(1.0, takeAmount);
            highp float blendWeight = takeAmount / (accumDepth + takeAmount);
            lerpLatent(outputLatent, latentThere, blendWeight, outputLatent);
            accumDepth += takeAmount;
        }
    }
    pctThere = float(pullParams0123.b) / 60000.0;
    if (pctThere > 0.0) {
        highp ivec2 posThere = ivec2(workingPos.x+1, workingPos.y+1);;
        highp uint depthThere = texelFetch(u_workingWetDepthTexNum, posThere, 0).r;
        highp float giveFracThere = float(texelFetch(u_accumWetDepthTexNum, posThere, 0).r) / 60000.0;
        highp float takeAmount = float(depthThere) * giveFracThere * pctThere;
        if (takeAmount > 0.0) {
            highp uvec4 latentCThere = texelFetch(u_workingLatentCTexNum, posThere, 0);
            highp uvec4 latentMThere = texelFetch(u_workingLatentMTexNum, posThere, 0);
            highp float latentThere[8];
            latentFromLatentCM(latentCThere, latentMThere, latentThere);
            takeAmount = max(1.0, takeAmount);
            highp float blendWeight = takeAmount / (accumDepth + takeAmount);
            lerpLatent(outputLatent, latentThere, blendWeight, outputLatent);
            accumDepth += takeAmount;
        }
    }
    pctThere = float(pullParams0123.a) / 60000.0;
    if (pctThere > 0.0) {
        highp ivec2 posThere = ivec2(workingPos.x-1, workingPos.y);;
        highp uint depthThere = texelFetch(u_workingWetDepthTexNum, posThere, 0).r;
        highp float giveFracThere = float(texelFetch(u_accumWetDepthTexNum, posThere, 0).r) / 60000.0;
        highp float takeAmount = float(depthThere) * giveFracThere * pctThere;
        if (takeAmount > 0.0) {
            highp uvec4 latentCThere = texelFetch(u_workingLatentCTexNum, posThere, 0);
            highp uvec4 latentMThere = texelFetch(u_workingLatentMTexNum, posThere, 0);
            highp float latentThere[8];
            latentFromLatentCM(latentCThere, latentMThere, latentThere);
            takeAmount = max(1.0, takeAmount);
            highp float blendWeight = takeAmount / (accumDepth + takeAmount);
            lerpLatent(outputLatent, latentThere, blendWeight, outputLatent);
            accumDepth += takeAmount;
        }
    }
    pctThere = float(pullParams5678.r) / 60000.0;
    if (pctThere > 0.0) {
        highp ivec2 posThere = ivec2(workingPos.x+1, workingPos.y);;
        highp uint depthThere = texelFetch(u_workingWetDepthTexNum, posThere, 0).r;
        highp float giveFracThere = float(texelFetch(u_accumWetDepthTexNum, posThere, 0).r) / 60000.0;
        highp float takeAmount = float(depthThere) * giveFracThere * pctThere;
        if (takeAmount > 0.0) {
            highp uvec4 latentCThere = texelFetch(u_workingLatentCTexNum, posThere, 0);
            highp uvec4 latentMThere = texelFetch(u_workingLatentMTexNum, posThere, 0);
            highp float latentThere[8];
            latentFromLatentCM(latentCThere, latentMThere, latentThere);
            takeAmount = max(1.0, takeAmount);
            highp float blendWeight = takeAmount / (accumDepth + takeAmount);
            lerpLatent(outputLatent, latentThere, blendWeight, outputLatent);
            accumDepth += takeAmount;
        }
    }
    pctThere = float(pullParams5678.g) / 60000.0;
    if (pctThere > 0.0) {
        highp ivec2 posThere = ivec2(workingPos.x-1, workingPos.y-1);;
        highp uint depthThere = texelFetch(u_workingWetDepthTexNum, posThere, 0).r;
        highp float giveFracThere = float(texelFetch(u_accumWetDepthTexNum, posThere, 0).r) / 60000.0;
        highp float takeAmount = float(depthThere) * giveFracThere * pctThere;
        if (takeAmount > 0.0) {
            highp uvec4 latentCThere = texelFetch(u_workingLatentCTexNum, posThere, 0);
            highp uvec4 latentMThere = texelFetch(u_workingLatentMTexNum, posThere, 0);
            highp float latentThere[8];
            latentFromLatentCM(latentCThere, latentMThere, latentThere);
            takeAmount = max(1.0, takeAmount);
            highp float blendWeight = takeAmount / (accumDepth + takeAmount);
            lerpLatent(outputLatent, latentThere, blendWeight, outputLatent);
            accumDepth += takeAmount;
        }
    }
    pctThere = float(pullParams5678.b) / 60000.0;
    if (pctThere > 0.0) {
        highp ivec2 posThere = ivec2(workingPos.x, workingPos.y-1);;
        highp uint depthThere = texelFetch(u_workingWetDepthTexNum, posThere, 0).r;
        highp float giveFracThere = float(texelFetch(u_accumWetDepthTexNum, posThere, 0).r) / 60000.0;
        highp float takeAmount = float(depthThere) * giveFracThere * pctThere;
        if (takeAmount > 0.0) {
            highp uvec4 latentCThere = texelFetch(u_workingLatentCTexNum, posThere, 0);
            highp uvec4 latentMThere = texelFetch(u_workingLatentMTexNum, posThere, 0);
            highp float latentThere[8];
            latentFromLatentCM(latentCThere, latentMThere, latentThere);
            takeAmount = max(1.0, takeAmount);
            highp float blendWeight = takeAmount / (accumDepth + takeAmount);
            lerpLatent(outputLatent, latentThere, blendWeight, outputLatent);
            accumDepth += takeAmount;
        }
    }
    pctThere = float(pullParams5678.a) / 60000.0;
    if (pctThere > 0.0) {
        highp ivec2 posThere = ivec2(workingPos.x+1, workingPos.y-1);;
        highp uint depthThere = texelFetch(u_workingWetDepthTexNum, posThere, 0).r;
        highp float giveFracThere = float(texelFetch(u_accumWetDepthTexNum, posThere, 0).r) / 60000.0;
        highp float takeAmount = float(depthThere) * giveFracThere * pctThere;
        if (takeAmount > 0.0) {
            highp uvec4 latentCThere = texelFetch(u_workingLatentCTexNum, posThere, 0);
            highp uvec4 latentMThere = texelFetch(u_workingLatentMTexNum, posThere, 0);
            highp float latentThere[8];
            latentFromLatentCM(latentCThere, latentMThere, latentThere);
            takeAmount = max(1.0, takeAmount);
            highp float blendWeight = takeAmount / (accumDepth + takeAmount);
            lerpLatent(outputLatent, latentThere, blendWeight, outputLatent);
            accumDepth += takeAmount;
        }
    }

        
    outputDepth = uint(accumDepth + 0.5);
    outputLatentC = latentToLatentC(outputLatent);
    outputLatentM = latentToLatentM(outputLatent);

    return;
}
`;


static fsSourceWetChangesToVisibleChanges = `#version 300 es
#line 10863
precision highp float;
precision highp int;
//precision highp uint;
uniform highp usampler2D u_workingLatentCTexNum;
uniform highp usampler2D u_workingLatentMTexNum;
uniform highp usampler2D u_workingWetDepthTexNum;
uniform highp usampler2D u_workingWetAgeTexNum;
uniform highp usampler2D u_accumLatentCTexNum;
uniform highp usampler2D u_accumLatentMTexNum;
uniform highp usampler2D u_accumWetDepthTexNum;
uniform highp usampler2D u_referenceLatentCTexNum;
uniform highp usampler2D u_referenceLatentMTexNum;
uniform highp usampler2D u_referenceWetDepthTexNum;
uniform highp int u_dryTransmits;
in highp vec2 v_texCoordRendered;
in highp vec2 v_texCoordExternal;
layout(location = 0) out highp uvec4 outputLatentC;
layout(location = 1) out highp uvec4 outputLatentM;
layout(location = 2) out highp uint outputDepth;
layout(location = 3) out highp uint outputAge;

highp float preferredMeniscusScrapeDepth = 8.0 * 4.0; 
highp float preferredSmashScrapeDepth = 3.0 * 4.0; 
highp float backgroundImplicitDepth = 1.5;
`  
+ TouchPageChange.fsColorMixLerpLatentSource  
+`
#line 10878

void main() {
    // make the new wet paint mask. source is the new wet paint and accumulator is the starting
    highp uvec4 newWetLatentC = texture(u_workingLatentCTexNum, v_texCoordRendered);
    highp uvec4 newWetLatentM = texture(u_workingLatentMTexNum, v_texCoordRendered);
    highp uvec4 newWetDepth = texture(u_workingWetDepthTexNum, v_texCoordRendered);
    highp uvec4 startWetLatentC = texture(u_accumLatentCTexNum, v_texCoordRendered);
    highp uvec4 startWetLatentM = texture(u_accumLatentMTexNum, v_texCoordRendered);
    highp uvec4 startWetDepth = texture(u_accumWetDepthTexNum, v_texCoordRendered);
    highp uint startWetAge = texture(u_workingWetAgeTexNum, v_texCoordRendered).r;

    outputAge = startWetAge;
    outputDepth = 0u;
    outputLatentC = emptyLatentPart;
    outputLatentM = emptyLatentPart;

    if (newWetDepth.r == startWetDepth.r && latentPartMatch(newWetLatentC, startWetLatentC, newWetLatentM, startWetLatentM)) {
        return;
    }
    
    highp uvec4 dryLatentC = texture(u_referenceLatentCTexNum, v_texCoordRendered);
    highp uvec4 dryLatentM = texture(u_referenceLatentMTexNum, v_texCoordRendered);

    if (newWetDepth.r == 0u) {
        outputLatentC = dryLatentC;
        outputLatentM = dryLatentM;
        outputAge = 0u;
        return;
    }
    outputAge = 8u;
        
    if (u_dryTransmits == 0) {
        outputLatentC = newWetLatentC;
        outputLatentM = newWetLatentM;
        return;
    }
    // blend
    // scale between depth and 4* depth so thicker paint blocks out effect of blending much more
    highp float scaledDepth = float(newWetDepth.r);
    if (scaledDepth > preferredSmashScrapeDepth) {
        scaledDepth = 4.0 * preferredMeniscusScrapeDepth * ((scaledDepth-preferredSmashScrapeDepth)/(preferredMeniscusScrapeDepth - preferredSmashScrapeDepth));
    }
    highp float depthRat = backgroundImplicitDepth / (scaledDepth + backgroundImplicitDepth);
    highp float outputLatent[8];
    latentFromLatentCM(dryLatentC, dryLatentM, outputLatent);
    highp float newLatent[8];
    latentFromLatentCM(newWetLatentC, newWetLatentM, newLatent);
    lerpLatent(newLatent, outputLatent, depthRat, outputLatent);
    outputLatentC = latentToLatentC(outputLatent);
    outputLatentM = latentToLatentM(outputLatent);
    
    return;
}
`;

static fsSourceVisibleChangesToFinal = `#version 300 es
#line 10945
precision highp float;
precision highp int;
//precision highp uint;
uniform highp usampler2D u_workingLatentCTexNum;
uniform highp usampler2D u_workingLatentMTexNum;
uniform highp usampler2D u_workingWetDepthTexNum;
uniform highp usampler2D u_accumLatentCTexNum;
uniform highp usampler2D u_accumLatentMTexNum;
uniform highp usampler2D u_accumWetDepthTexNum;
in highp vec2 v_texCoordRendered;
in highp vec2 v_texCoordExternal;
uniform highp int u_doAntiAlias;
layout(location = 0) out highp uvec4 outputLatentC;
layout(location = 1) out highp uvec4 outputLatentM;

`  
+ TouchPageChange.fsColorMixLerpLatentSource  
+`
#line 10948

void accumulateInfluence(highp uvec4 inflLatentC, highp uvec4 inflLatentM, highp float inf, inout highp float influenceLatent[8], inout highp float votes) {
    if (!(inflLatentC == emptyLatentPart && inflLatentM == emptyLatentPart)) {
        highp float l[8];
        latentFromLatentCM(inflLatentC, inflLatentM, l);
        if (votes == 0.0) {
            votes=inf;
            influenceLatent = l;
        } else {
            // blend
            votes+=inf;
            if (influenceLatent != l) {
                highp float blendWeight = inf / votes;
                lerpLatent(influenceLatent, l, blendWeight, influenceLatent);
            }
        }
    }
}

void main() {
    // produce the final visible.
    // source is external visible
    // accumulator is the new visible mask from step 6
    highp uvec4 newVisibleLatentC = texture(u_accumLatentCTexNum, v_texCoordRendered);
    highp uvec4 newVisibleLatentM = texture(u_accumLatentMTexNum, v_texCoordRendered);
    highp uvec4 oldVisibleLatentC = texture(u_workingLatentCTexNum, v_texCoordRendered);
    highp uvec4 oldVisibleLatentM = texture(u_workingLatentMTexNum, v_texCoordRendered);

    highp uvec2 workingSize = uvec2(textureSize(u_accumLatentCTexNum, 0));
    highp ivec2 workingPos = ivec2(v_texCoordRendered * vec2(workingSize));

    if (!(newVisibleLatentC == emptyLatentPart && newVisibleLatentM == emptyLatentPart)) {
        outputLatentC = newVisibleLatentC;
        outputLatentM = newVisibleLatentM;
    } else {
        outputLatentC = oldVisibleLatentC;
        outputLatentM = oldVisibleLatentM;

        if (u_doAntiAlias == 1) {
            // if next to any new visibles, collect the influence for anti aliasing
            highp float influenceLatent[8] = smudgeLatent;
            highp float votes = 0.0;

            highp uvec4 inflLatentC = texelFetch(u_accumLatentCTexNum, workingPos + ivec2(-1, -1), 0);
            highp uvec4 inflLatentM = texelFetch(u_accumLatentMTexNum, workingPos + ivec2(-1, -1), 0);
            accumulateInfluence(inflLatentC, inflLatentM, 0.707, influenceLatent, votes);
            inflLatentC = texelFetch(u_accumLatentCTexNum, workingPos + ivec2(0, -1), 0);
            inflLatentM = texelFetch(u_accumLatentMTexNum, workingPos + ivec2(0, -1), 0);
            accumulateInfluence(inflLatentC, inflLatentM, 1.0, influenceLatent, votes);
            inflLatentC = texelFetch(u_accumLatentCTexNum, workingPos + ivec2(1, -1), 0);
            inflLatentM = texelFetch(u_accumLatentMTexNum, workingPos + ivec2(1, -1), 0);
            accumulateInfluence(inflLatentC, inflLatentM, 0.707, influenceLatent, votes);
            inflLatentC = texelFetch(u_accumLatentCTexNum, workingPos + ivec2(-1, 0), 0);
            inflLatentM = texelFetch(u_accumLatentMTexNum, workingPos + ivec2(-1, 0), 0);
            accumulateInfluence(inflLatentC, inflLatentM, 1.0, influenceLatent, votes);
            inflLatentC = texelFetch(u_accumLatentCTexNum, workingPos + ivec2(1, 0), 0);
            inflLatentM = texelFetch(u_accumLatentMTexNum, workingPos + ivec2(1, 0), 0);
            accumulateInfluence(inflLatentC, inflLatentM, 0.707, influenceLatent, votes);
            inflLatentC = texelFetch(u_accumLatentCTexNum, workingPos + ivec2(-1, 1), 0);
            inflLatentM = texelFetch(u_accumLatentMTexNum, workingPos + ivec2(-1, 1), 0);
            accumulateInfluence(inflLatentC, inflLatentM, 0.707, influenceLatent, votes);
            inflLatentC = texelFetch(u_accumLatentCTexNum, workingPos + ivec2(0, 1), 0);
            inflLatentM = texelFetch(u_accumLatentMTexNum, workingPos + ivec2(0, 1), 0);
            accumulateInfluence(inflLatentC, inflLatentM, 1.0, influenceLatent, votes);
            inflLatentC = texelFetch(u_accumLatentCTexNum, workingPos + ivec2(1, 1), 0);
            inflLatentM = texelFetch(u_accumLatentMTexNum, workingPos + ivec2(1, 1), 0);
            accumulateInfluence(inflLatentC, inflLatentM, 0.707, influenceLatent, votes);

            if (votes > 0.0) {
                // blend
                highp float depthRat = votes / 10.0;
                highp float outputLatent[8];
                latentFromLatentCM(outputLatentC, outputLatentM, outputLatent);
                lerpLatent(outputLatent, influenceLatent, depthRat, outputLatent);
                outputLatentC = latentToLatentC(outputLatent);
                outputLatentM = latentToLatentM(outputLatent);
                //outputLatentM = neutralLatentM;
                //outputLatentC = cyanLatentC;
            }
        }
    }
    return;
}
`;


static fsSourceDebugRenderDepth = `#version 300 es
#line 11050
precision highp float;
precision highp int;
//precision highp uint;
uniform highp usampler2D u_workingLatentCTexNum;
uniform highp usampler2D u_workingLatentMTexNum;
uniform highp usampler2D u_workingWetDepthTexNum;
uniform highp usampler2D u_kmLookupTexNum;
in highp vec2 v_texCoordRendered;
in highp vec2 v_texCoordExternal;
layout(location = 0) out highp vec4 outputColor;
layout(location = 1) out highp uint outputDepth;
` + TouchPageChange.fsColorMixLatentToRGBSource + `
#line 11044
void main() {
    outputDepth = texture(u_workingWetDepthTexNum, v_texCoordRendered).r;
    if (outputDepth == 0u) {
        outputColor = vec4(0.0, 0.0, 0.0, 1.0);
    } else if (outputDepth < 5u) {
        outputColor = vec4(1.0, 1.0, 1.0, 1.0);
    } else if (outputDepth < 11u) {
        outputColor = vec4(1.0, 0.0, 0.0, 1.0);
    } else if (outputDepth < 21u) {
        outputColor = vec4(1.0, 0.5, 0.0, 1.0);
    } else if (outputDepth < 36u) {
        outputColor = vec4(1.0, 1.0, 0.0, 1.0);
    } else if (outputDepth < 66u) {
        outputColor = vec4(0.0, 1.0, 0.0, 1.0);
    } else if (outputDepth < 150u) {
        outputColor = vec4(0.0, 0.5, 0.9, 1.0);
    } else if (outputDepth < 500u) {
        outputColor = vec4(0.0, 0.0, 1.0, 1.0);
    } else if (outputDepth < 2001u) {
        outputColor = vec4(0.0, 0.5, 0.9, 1.0);
    } else {
        outputColor = vec4(0.5, 0.5, 0.5, 1.0);
    }
}`;

static fsSourceDebugTypeEffects =
    TouchPageChange.fsSourceProcessGlobals  
    + TouchPageChange.fsColorMixLerpLatentSource
    +
`
#line 11075

void main() {
    
    outputDepth = texture(u_workingWetDepthTexNum, v_texCoordRendered).r;
    outputLatentC = texture(u_workingLatentCTexNum, v_texCoordRendered);
    outputLatentM = texture(u_workingLatentMTexNum, v_texCoordRendered);
    if (outputDepth == 0u) {
        return;
    }
   
    uvec4 mustC = mustardLatentC;
    mustC.g = 62053u;
    uvec4 mustM = neutralLatentM;
    highp float l[8];
    latentFromLatentCM(mustC, mustM, l);

    l[1] = float(62053u) / u_latentScale * (u_maxC1 - u_minC1) + u_minC1;
    highp float mc0 = float(62053u) / u_latentScale * (u_maxC1 - u_minC1) + u_minC1;
    
    highp float cdiff =  mc0-l[1];
    if (cdiff * 4000.0 > 1.0) {
        outputLatentC = mustardLatentC;
        outputLatentM = neutralLatentM;
        return;
    }
    //l.concentration1 = 0.9383388289;
    //mediump float cy = 0.9383388289;
    //mediump float c1 = clamp(cy, u_minC1, u_maxC1);
    //c1 = (c1 - u_minC1) / (u_maxC1 - u_minC1);
    //highp uint c1u = uint((c1 * u_latentScale) + 0.5);

    uvec4 convertedC = latentToLatentC(l);
    if (mustC.g - convertedC.g > 20u) {
        outputLatentC = cyanLatentC;
        outputLatentM = neutralLatentM;
    }
    
    return;
}
`;
    static getPrograms(): Array<[string, ProgramInfo]> {
        return [[TouchPageChange.fsSourceExternalToLatent, TouchPageChange.externalToLatentProgram],
            [TouchPageChange.fsSourceLatentToExternal, TouchPageChange.latentToExternalProgram], 
            [TouchPageChange.fsSourceProcessAccumulateTakes, TouchPageChange.processAccumulateTakesProgram], 
            [TouchPageChange.fsSourceProcessCommitAccumulator, TouchPageChange.processCommitAccumulatorProgram], 
            [TouchPageChange.fsSourceWetChangesToVisibleChanges, TouchPageChange.wetChangesToVisibleChangesProgram],
            [TouchPageChange.fsSourceVisibleChangesToFinal, TouchPageChange.visibleChangesToFinalProgram],
            [TouchPageChange.fsSourceCentralPullWetChanges, TouchPageChange.centralPullWetChangesProgram],
            [TouchPageChange.fsSourceDebugRenderDepth, TouchPageChange.debugRenderDepth],
            [TouchPageChange.fsSourceDebugTypeEffects, TouchPageChange.debugTypeEffects],
            
        ];
    }

    static initProgramLocations(gcFrom: GPUChecker) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'touch-initProgramLocations');
        const gl = gc.gl;

        TouchPageChange.externalToLatentProgram.uniformLocations = {} as TouchUniformLocations;
        gc.check('1', ()=>TouchPageChange.addStartupProgramLocations(gc, TouchPageChange.externalToLatentProgram));

        TouchPageChange.latentToExternalProgram.uniformLocations = {} as TouchUniformLocations;
        gc.check('2', ()=>TouchPageChange.addStartupProgramLocations(gc, TouchPageChange.latentToExternalProgram));
        gc.check('3', ()=>TouchPageChange.addFinalProgramLocations(gc, TouchPageChange.latentToExternalProgram));

        TouchPageChange.processAccumulateTakesProgram.uniformLocations = {} as TouchUniformLocations;
        gc.check('4', ()=>TouchPageChange.addProcessProgramLocations(gc, TouchPageChange.processAccumulateTakesProgram));

        TouchPageChange.processCommitAccumulatorProgram.uniformLocations = {} as TouchUniformLocations;
        gc.check('5', ()=>TouchPageChange.addProcessProgramLocations(gc, TouchPageChange.processCommitAccumulatorProgram));

        TouchPageChange.wetChangesToVisibleChangesProgram.uniformLocations = {} as TouchUniformLocations;
        gc.check('6', ()=>TouchPageChange.addFinishProgramLocations(gc, TouchPageChange.wetChangesToVisibleChangesProgram));

        TouchPageChange.visibleChangesToFinalProgram.uniformLocations = {} as TouchUniformLocations;
        gc.check('7', ()=>TouchPageChange.addFinishProgramLocations(gc, TouchPageChange.visibleChangesToFinalProgram));

        TouchPageChange.centralPullWetChangesProgram.uniformLocations = {} as TouchUniformLocations;
        gc.check('8', ()=>TouchPageChange.addFinishProgramLocations(gc, TouchPageChange.centralPullWetChangesProgram));

        TouchPageChange.debugRenderDepth.uniformLocations = {} as TouchUniformLocations;
        gc.check('9', ()=>TouchPageChange.addStartupProgramLocations(gc, TouchPageChange.debugRenderDepth));

        TouchPageChange.debugTypeEffects.uniformLocations = {} as TouchUniformLocations;
        gc.check('10', ()=>TouchPageChange.addFinishProgramLocations(gc, TouchPageChange.debugTypeEffects));

    }

    static addSharedProgramLocations(gcFrom: GPUChecker, programInfo: TouchProgramInfo) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'touch-addSharedProgramLocations');
        const gl = gc.gl;

        programInfo.uniformLocations.kmLookupTexNum = gc.checkAPI('1', ()=>gl.getUniformLocation(programInfo.program, 'u_kmLookupTexNum')!) as WebGLUniformLocation;
        programInfo.uniformLocations.workingLatentCTexNum = gc.checkAPI('2', ()=>gl.getUniformLocation(programInfo.program, 'u_workingLatentCTexNum')!) as WebGLUniformLocation;;
        programInfo.uniformLocations.workingLatentMTexNum = gc.checkAPI('3', ()=>gl.getUniformLocation(programInfo.program, 'u_workingLatentMTexNum')!) as WebGLUniformLocation;;
        programInfo.uniformLocations.workingWetDepthTexNum = gc.checkAPI('4', ()=>gl.getUniformLocation(programInfo.program, 'u_workingWetDepthTexNum')!) as WebGLUniformLocation;;
        programInfo.uniformLocations.workingDryDepthTexNum = gc.checkAPI('5', ()=>gl.getUniformLocation(programInfo.program, 'u_workingDryDepthTexNum')!) as WebGLUniformLocation;;
        programInfo.uniformLocations.accumLatentCTexNum = gc.checkAPI('6', ()=>gl.getUniformLocation(programInfo.program, 'u_accumLatentCTexNum')!) as WebGLUniformLocation;;
        programInfo.uniformLocations.accumLatentMTexNum = gc.checkAPI('7', ()=>gl.getUniformLocation(programInfo.program, 'u_accumLatentMTexNum')!) as WebGLUniformLocation;;
        programInfo.uniformLocations.accumWetDepthTexNum = gc.checkAPI('8', ()=>gl.getUniformLocation(programInfo.program, 'u_accumWetDepthTexNum')!) as WebGLUniformLocation;;
        programInfo.uniformLocations.accumDryDepthTexNum = gc.checkAPI('9', ()=>gl.getUniformLocation(programInfo.program, 'u_accumDryDepthTexNum')!) as WebGLUniformLocation;;
        programInfo.uniformLocations.referenceLatentCTexNum = gc.checkAPI('10', ()=>gl.getUniformLocation(programInfo.program, 'u_referenceLatentCTexNum')!) as WebGLUniformLocation;;
        programInfo.uniformLocations.referenceLatentMTexNum = gc.checkAPI('11', ()=>gl.getUniformLocation(programInfo.program, 'u_referenceLatentMTexNum')!) as WebGLUniformLocation;;
        programInfo.uniformLocations.referenceWetDepthTexNum = gc.checkAPI('12', ()=>gl.getUniformLocation(programInfo.program, 'u_referenceWetDepthTexNum')!) as WebGLUniformLocation;;
        programInfo.uniformLocations.referenceDryDepthTexNum = gc.checkAPI('13', ()=>gl.getUniformLocation(programInfo.program, 'u_referenceDryDepthTexNum')!) as WebGLUniformLocation;;

    }

    static addStartupProgramLocations(gcFrom: GPUChecker, programInfo: TouchProgramInfo) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'touch-addStartupProgramLocations');
        const gl = gc.gl;

        gc.check('1', ()=>TouchPageChange.addSharedProgramLocations(gc, programInfo));
        gc.check('2', ()=>PageChange.addProgramLocations(gc, programInfo, true));
    }

    static addProcessProgramLocations(gcFrom: GPUChecker, programInfo: TouchProgramInfo) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'touch-addProcessProgramLocations');
        const gl = gc.gl;

        gc.check('1', ()=>TouchPageChange.addSharedProgramLocations(gc, programInfo));
        programInfo.uniformLocations.maxDepthTexNum = gc.checkAPI('2', ()=>gl.getUniformLocation(programInfo.program, 'u_maxDepthTexNum')!) as WebGLUniformLocation;
        programInfo.uniformLocations.actionCommitTexNum = gc.checkAPI('3', ()=>gl.getUniformLocation(programInfo.program, 'u_actionCommitTexNum')!) as WebGLUniformLocation;;
        programInfo.uniformLocations.actionFromTexNum = gc.checkAPI('4', ()=>gl.getUniformLocation(programInfo.program, 'u_actionFromTexNum')!) as WebGLUniformLocation;;
        programInfo.uniformLocations.actionFracTexNum = gc.checkAPI('5', ()=>gl.getUniformLocation(programInfo.program, 'u_actionFracTexNum')!) as WebGLUniformLocation;;
        programInfo.uniformLocations.maxDepthTextureIndex = gc.checkAPI('6', ()=>gl.getUniformLocation(programInfo.program, 'u_maxDepthTextureIndex')!) as WebGLUniformLocation;;
        programInfo.uniformLocations.actionTextureIndex = gc.checkAPI('7', ()=>gl.getUniformLocation(programInfo.program, 'u_actionTextureIndex')!) as WebGLUniformLocation;;
        programInfo.uniformLocations.writeAccum = gc.checkAPI('8', ()=>gl.getUniformLocation(programInfo.program, 'u_writeAccum')!) as WebGLUniformLocation;
        programInfo.uniformLocations.debug = gc.checkAPI('9', ()=>gl.getUniformLocation(programInfo.program, 'u_debug')!) as WebGLUniformLocation;
        programInfo.uniformLocations.actionTextureOffset = gc.checkAPI('10', ()=>gl.getUniformLocation(programInfo.program, 'u_actionTextureOffset')!) as WebGLUniformLocation;;
        programInfo.uniformLocations.maxDepthTextureOffset = gc.checkAPI('11', ()=>gl.getUniformLocation(programInfo.program, 'u_maxDepthTextureOffset')!) as WebGLUniformLocation;;

        gc.check('12', ()=>PageChange.addProgramLocations(gc, programInfo, true));
    }

    static addFinishProgramLocations(gcFrom: GPUChecker, programInfo: TouchProgramInfo) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'touch-addFinishProgramLocations');
        const gl = gc.gl;

        gc.check('1', ()=>TouchPageChange.addSharedProgramLocations(gc, programInfo));
        programInfo.uniformLocations.dryTransmits = gc.checkAPI('2', ()=>gl.getUniformLocation(programInfo.program, 'u_dryTransmits')!) as WebGLUniformLocation;
        programInfo.uniformLocations.doAntiAlias = gc.checkAPI('3', ()=>gl.getUniformLocation(programInfo.program, 'u_doAntiAlias')!) as WebGLUniformLocation;
        programInfo.uniformLocations.workingWetAgeTexNum = gc.checkAPI('4', ()=>gl.getUniformLocation(programInfo.program, 'u_workingWetAgeTexNum')!) as WebGLUniformLocation;;
        gc.check('5', ()=>PageChange.addProgramLocations(gc, programInfo, true));
    }

    static addFinalProgramLocations(gcFrom: GPUChecker, programInfo: TouchProgramInfo) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'touch-addFinalProgramLocations');
        const gl = gc.gl;

        programInfo.uniformLocations.doDepthLighting = gc.checkAPI('', ()=>gl.getUniformLocation(programInfo.program, 'u_doDepthLighting')!, true) as WebGLUniformLocation;;
    }

    static initPermanentStorage(gcFrom: GPUChecker, textureCallback: (radius:number, application: TipApplication) => ChangeTextureSet) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'touch-pass');
        TouchPageChange.touchBuffers = {} as TouchProgramBuffers;
        TouchPageChange.addPermanentStorage(gc, TouchPageChange.touchBuffers, textureCallback);
    }
    static addPermanentStorage(gcFrom: GPUChecker, buffers: TouchProgramBuffers, textureCallback: (radius:number, application: TipApplication) => ChangeTextureSet) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'touch-addPermanentStorage');
        const gl = gc.gl;

        gc.check('1', ()=>PageChange.addPermanentStorage(gc, buffers, textureCallback));
        if (TouchPageChange.touchBuffers!.kmLookupTexture == undefined) {

            TouchPageChange.touchBuffers!.kmLookupTexture = gc.checkAPI('18', ()=>GPURunner.initTexture(gc, 'kmLookupTexture'), true) as WebGLTexture;;
            TouchPageChange.touchBuffers!.nullDepthTexture = gc.checkAPI('19', ()=>GPURunner.initTexture(gc, 'nullDepthTexture'), true) as WebGLTexture;;
            TouchPageChange.touchBuffers!.nullLatentCTexture = gc.checkAPI('20', ()=>GPURunner.initTexture(gc, 'nullLatentCTexture'), true) as WebGLTexture;;
            TouchPageChange.touchBuffers!.nullLatentMTexture = gc.checkAPI('21', ()=>GPURunner.initTexture(gc, 'nullLatentMTexture'), true) as WebGLTexture;;

            let lut = getLookUpTable();
            gc.checkAPI('27', ()=>gl.activeTexture(gl.TEXTURE0 + TouchPageChange.kmLookupTexNumVal));
            gc.checkAPI('28', ()=>gl.bindTexture(gl.TEXTURE_2D, TouchPageChange.touchBuffers!.kmLookupTexture));
            gc.checkAPI('29', ()=>gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8UI, 1024, 773, 0, gl.RED_INTEGER, gl.UNSIGNED_BYTE, lut));

            gc.checkAPI('30', ()=>gl.activeTexture(gl.TEXTURE0 + TouchPageChange.accumWetDepthTexNumVal));
            gc.checkAPI('31', ()=>gl.bindTexture(gl.TEXTURE_2D, TouchPageChange.touchBuffers!.nullDepthTexture));
            gc.checkAPI('32', ()=>gl.texImage2D(gl.TEXTURE_2D, 0, gl.R16UI,
                2, 2, 0,
                gl.RED_INTEGER, gl.UNSIGNED_SHORT, null));

            gc.checkAPI('33', ()=>gl.activeTexture(gl.TEXTURE0 + TouchPageChange.accumLatentCTexNumVal));
            gc.checkAPI('34', ()=>gl.bindTexture(gl.TEXTURE_2D, TouchPageChange.touchBuffers!.nullLatentCTexture));
            gc.checkAPI('35', ()=>gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA16UI,
                2, 2, 0,
                gl.RGBA_INTEGER, gl.UNSIGNED_SHORT, null));
            gc.checkAPI('36', ()=>gl.activeTexture(gl.TEXTURE0 + TouchPageChange.accumLatentMTexNumVal));
            gc.checkAPI('37', ()=>gl.bindTexture(gl.TEXTURE_2D, TouchPageChange.touchBuffers!.nullLatentMTexture));
            gc.checkAPI('38', ()=>gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA16UI,
                2, 2, 0,
                gl.RGBA_INTEGER, gl.UNSIGNED_SHORT, null));
    
        }

        buffers.kmLookupTexture = TouchPageChange.touchBuffers!.kmLookupTexture;
        buffers.nullDepthTexture = TouchPageChange.touchBuffers!.nullDepthTexture;
        buffers.nullLatentCTexture = TouchPageChange.touchBuffers!.nullLatentCTexture;
        buffers.nullLatentMTexture = TouchPageChange.touchBuffers!.nullLatentMTexture;
    }

    // shared
    static workingLatentCTexNumVal = 0;
    static workingLatentMTexNumVal = 1;
    static workingWetDepthTexNumVal = 2;
    static workingDryDepthTexNumVal = 3;
    static workingWetAgeTexNumVal = 4;
    static accumLatentCTexNumVal = 5;
    static accumLatentMTexNumVal = 6;
    static accumWetDepthTexNumVal = 7;
    static accumDryDepthTexNumVal = 8;
    static referenceLatentCTexNumVal = 9;
    static referenceLatentMTexNumVal = 10;
    static referenceWetDepthTexNumVal = 11;
    static referenceDryDepthTexNumVal = 12;
    static kmLookupTexNumVal = 13;
    // process only
    static maxDepthTexNumVal = 14;
    static actionFromTexNumVal = 15;
    static actionFracTexNumVal = 16;
    static actionCommitTexNumVal = 17;

    // finish only



    entries: Array<PageChangeEntry>;
    tipApplication: TipApplication;
    dryTransmits: boolean;
    centralPull: ((d:number)=>number) | undefined

    activeWorkingLatentCTexture: WebGLTexture | null = null;
    activeWorkingLatentMTexture: WebGLTexture | null = null;
    activeWorkingDepthTexture: WebGLTexture | null = null;
    activeWorkingFramebuffer: WebGLFramebuffer | null = null;
    activeAccLatentCTexture: WebGLTexture | null = null;
    activeAccLatentMTexture: WebGLTexture | null = null;
    activeAccDepthTexture: WebGLTexture | null = null;
    activeAccFramebuffer: WebGLFramebuffer | null = null;
    activeOutputLatentCTexture: WebGLTexture | null = null;
    activeOutputLatentMTexture: WebGLTexture | null = null;
    activeOutputDepthTexture: WebGLTexture | null = null;
    activeOutputFramebuffer: WebGLFramebuffer | null = null;
    activeStashedLatentCTexture: WebGLTexture | null = null;
    activeStashedLatentMTexture: WebGLTexture | null = null;
    activeStashedDepthTexture: WebGLTexture | null = null;
    activeStashedFramebuffer: WebGLFramebuffer | null = null;
    activePreservedLatentCTexture: WebGLTexture | null = null;
    activePreservedLatentMTexture: WebGLTexture | null = null;
    activePreservedDepthTexture: WebGLTexture | null = null;
    activePreservedFramebuffer: WebGLFramebuffer | null = null;

    initialWetLatentCTexture: WebGLTexture | null = null;
    initialWetLatentMTexture: WebGLTexture | null = null;
    initialWetDepthTexture: WebGLTexture | null = null;
    initialWetAgeTexture: WebGLTexture | null = null;
    working1LatentCTexture: WebGLTexture | null = null;
    working1LatentMTexture: WebGLTexture | null = null;
    working1DepthTexture: WebGLTexture | null = null;
    working1Framebuffer: WebGLFramebuffer | null = null;
    working2LatentCTexture: WebGLTexture | null = null;
    working2LatentMTexture: WebGLTexture | null = null;
    working2DepthTexture: WebGLTexture | null = null;
    working2Framebuffer: WebGLFramebuffer | null = null;
    working3LatentCTexture: WebGLTexture | null = null;
    working3LatentMTexture: WebGLTexture | null = null;
    working3DepthTexture: WebGLTexture | null = null;
    working3Framebuffer: WebGLFramebuffer | null = null;
    accLatentCTexture: WebGLTexture | null = null;
    accLatentMTexture: WebGLTexture | null = null;
    accDepthTexture: WebGLTexture | null = null;
    accFramebuffer: WebGLFramebuffer | null = null;
    finalWetAgeTexture: WebGLTexture | null = null;


    constructor(page: Page, pageX: number, pageY: number, width: number, height: number,
            tipsAndLocations: Array<PageChangeEntry>, tipApplication: TipApplication,
            dryTransmits: boolean, centralPull: ((d:number)=>number) | undefined) {
        super(page, pageX, pageY, width, height);
        this.entries = tipsAndLocations;
        this.tipApplication = tipApplication;
        this.dryTransmits = dryTransmits;
        this.centralPull = centralPull;
    }

    debugShowAt = 0;
    debugShowAt2 = 0;

    debugRenderLatentOutput(piRestart: ProgramInfo, depthRender: boolean = false) {
        const gc = new GPUChecker(GPURunner.gl!, undefined, 'debugRenderLatentOutput');
        const gl = gc.gl;
        let saveInputLatentCTex = this.activeWorkingLatentCTexture;
        let saveInputLatentMTex = this.activeWorkingLatentMTexture;
        let useOutputLatentCTex = this.activeOutputLatentCTexture;
        let useOutputLatentMTex = this.activeOutputLatentMTexture;
        let saveInputDepthTex = this.activeWorkingDepthTexture;
        let useOutputDepthTex = this.activeOutputDepthTexture;
        if (!depthRender) {
            saveInputDepthTex = null;
            useOutputDepthTex = null;
        }
        if (depthRender === false) {
            gl.useProgram(TouchPageChange.latentToExternalProgram.program);
            this._initializeOutputProgram(gc, TouchPageChange.latentToExternalProgram, null as any);
        } else {
            gl.useProgram(TouchPageChange.debugRenderDepth.program);
            this._initializeOutputProgram(gc, TouchPageChange.debugRenderDepth, null as any);
        }
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        if (depthRender === false) {
            gl.activeTexture(gl.TEXTURE0 + TouchPageChange.workingLatentCTexNumVal);
            gl.bindTexture(gl.TEXTURE_2D, useOutputLatentCTex);
            gl.activeTexture(gl.TEXTURE0 + TouchPageChange.workingLatentMTexNumVal);
            gl.bindTexture(gl.TEXTURE_2D, useOutputLatentMTex);
        } else {
            gl.activeTexture(gl.TEXTURE0 + TouchPageChange.workingWetDepthTexNumVal);
            gl.bindTexture(gl.TEXTURE_2D, useOutputDepthTex);
        }
        gl.drawArrays(gl.TRIANGLES, 0, PageChange.verts);
        //gl.drawArrays(gl.TRIANGLES, 0, 24);
        this.crcDebug!.clearRect(this.debugShowAt, this.debugShowAt2, 50, 50);
        this.crcDebug!.drawImage(gl.canvas, this.debugShowAt, this.debugShowAt2);
        this.debugShowAt += 50;
        if (this.debugShowAt > 250) {
            this.debugShowAt = 0;
            this.debugShowAt2 += 50;
            if (this.debugShowAt2 > 250) {
                this.debugShowAt2 = 0;
            }

        }
        if (depthRender === false) {
            gl.activeTexture(gl.TEXTURE0 + TouchPageChange.workingLatentCTexNumVal);
            gl.bindTexture(gl.TEXTURE_2D, saveInputLatentCTex);
            gl.activeTexture(gl.TEXTURE0 + TouchPageChange.workingLatentMTexNumVal);
            gl.bindTexture(gl.TEXTURE_2D, saveInputLatentMTex);
        } else {
            gl.activeTexture(gl.TEXTURE0 + TouchPageChange.workingWetDepthTexNumVal);
            gl.bindTexture(gl.TEXTURE_2D, saveInputDepthTex);
        }
        gl.useProgram(piRestart.program);
    }

    debugMeasureDepthTexture(text: WebGLTexture, byte: boolean = false): [number, Uint8Array | Uint32Array] {
        const gl = GPURunner.gl!;
        let texDepth = this.debugGetIntTexture(text, this.width, this.height, 1, byte);
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.activeOutputFramebuffer);
        let d = 0;
        (texDepth.values() as any).forEach((element: number) => { d+=element; });
        return [d, texDepth];
    }
    debugSpewArray(buffer: Uint8Array | Uint32Array, intPer: number, cellWidth: number, scaleDown: number, altWidth: number | undefined = undefined, altHeight: number | undefined = undefined) {

        let width = altWidth === undefined ? this.width : altWidth;
        let height = altHeight === undefined ? this.height : altHeight;

        console.log('values');
        for (let i = height-1; i >= 0; i--) {
            let spew = '';
            if (i === height-1) {
                spew = '      ';
                for (let j = 0; j < width; j++) {
                    let cellSpew = `${j<10?' ':''}${j}`;
                    spew += ' '.repeat(cellWidth-cellSpew.length) + cellSpew + ',';
                }
                console.log(spew);
            }
            spew = `${i<10?' ':''}${i} ${(height-1-i)<10?' ':''}${(height-1-i)} `;
            for (let j = 0; j < width; j++) {
                let idx = i*intPer*height + j*intPer;
                let d = buffer[idx];
                let cellSpew = '';
                for (let k = 0; k < intPer; k++) {
                    d = buffer[idx+k];
                    cellSpew += (Math.floor(d/scaleDown) + (d > 0 && d <= scaleDown ? 1 : 0)).toString();
                }
                if (cellWidth < cellSpew.length) {
                    spew += cellSpew + ',';
                } else {
                    spew += ' '.repeat(cellWidth-cellSpew.length) + cellSpew + ',';
                }
    
            }
            console.log(spew);

        }
    }

    debugSpewDepthTexture(text: WebGLTexture, byte: boolean = false) {
        let d = this.debugMeasureDepthTexture(text, byte);
        if (byte) {
            this.debugSpewArray(d[1] as Uint8Array, 1, 4, 1);
        } else {
            this.debugSpewArray(d[1] as Uint32Array, 1, 4, 1);
        }
    }
    debugSpewLatentTexture(text: WebGLTexture) {
        const gl = GPURunner.gl!;
        let texLat = this.debugGetIntTexture(text, this.width, this.height, 4, false);
        //this.debugSpewArray(texLat, 4, 4, 6554);
        this.debugSpewArray(texLat, 4, 20, 1);
    }

    debugMeasureOutput() : [cntTot: number, cntFill: number, depth: number, c0: number, c1: number, c2: number, c3: number, mRed: number, mGreen: number, mBlue: number] {
        let latC = this.debugGetIntTexture(this.activeOutputLatentCTexture!, this.width, this.height, 4, false);
        let latM = this.debugGetIntTexture(this.activeOutputLatentMTexture!, this.width, this.height, 4, false);
        let dep = this.debugGetIntTexture(this.activeOutputDepthTexture!, this.width, this.height, 1, false);
        let cnt = 0;
        let totD = 0;
        let totC0 = 0;
        let totC1 = 0;
        let totC2 = 0;
        let totC3 = 0;
        let totM0 = 0;
        let totM1 = 0;
        let totM2 = 0;
        let totM3 = 0;
        for (let y = 0; y < this.height; y++) {
            for (let x = 0; x < this.width; x++) {
                let d = dep[(y * this.width + x)];
                totD += d;
                if (d > 0) {
                    let off = (y * this.width + x) * 4;
                    totC0 += d * latC[off];
                    totC1 += d * latC[off + 1];
                    totC2 += d * latC[off + 2];
                    totC3 += d * latC[off + 3];
                    totM0 += d * latM[off];
                    totM1 += d * latM[off + 1];
                    totM2 += d * latM[off + 2];
                    totM3 += d * latM[off + 3];
                    cnt++;
                }
            }
        }
        return [this.height*this.width, cnt, totD, totC0, totC1, totC2, totC3, totM0, totM1, totM2];
        

    }

    workingFrameTextureNumbers = [TouchPageChange.workingLatentCTexNumVal, TouchPageChange.workingLatentMTexNumVal, TouchPageChange.workingWetDepthTexNumVal];


    bindWorkingFrame(gc: GPUChecker, frame: WebGLFramebuffer | null = null, latentCTex: WebGLTexture | null = null, latentMTex: WebGLTexture | null = null, depthTex: WebGLTexture | null = null) {
        let gl = GPURunner.gl!;
        this.activeWorkingFramebuffer = frame;
        this.activeWorkingLatentCTexture = latentCTex;
        this.activeWorkingLatentMTexture = latentMTex;
        this.activeWorkingDepthTexture = depthTex;

        if (frame === null) {
            //console.log('bindWorking null frame');
            // indicates the working frame is external
            if (depthTex !== null) {
                gc.check('bwf1', ()=>GPURunner.activateTexture(gc, this.workingFrameTextureNumbers[2], depthTex));
            }
            if (latentMTex !== null) {
                gc.check('bwf2', ()=>GPURunner.activateTexture(gc, this.workingFrameTextureNumbers[1], latentMTex));
            }
            if (latentCTex !== null) {
                gc.check('bwf3', ()=>GPURunner.activateTexture(gc, this.workingFrameTextureNumbers[0], latentCTex));
            }

        } else {
            if (this.activeWorkingFramebuffer === this.working1Framebuffer) {
                this.activeWorkingDepthTexture = this.working1DepthTexture;
                this.activeWorkingLatentCTexture = this.working1LatentCTexture;
                this.activeWorkingLatentMTexture = this.working1LatentMTexture;
            } else if (this.activeWorkingFramebuffer === this.accFramebuffer) {
                this.activeWorkingDepthTexture = this.accDepthTexture;
                this.activeWorkingLatentCTexture = this.accLatentCTexture;
                this.activeWorkingLatentMTexture = this.accLatentMTexture;
            } else if (this.activeWorkingFramebuffer === this.working2Framebuffer) {
                this.activeWorkingDepthTexture = this.working2DepthTexture;
                this.activeWorkingLatentCTexture = this.working2LatentCTexture;
                this.activeWorkingLatentMTexture = this.working2LatentMTexture;
            } else if (this.activeWorkingFramebuffer === this.working3Framebuffer) {
                this.activeWorkingDepthTexture = this.working3DepthTexture;
                this.activeWorkingLatentCTexture = this.working3LatentCTexture;
                this.activeWorkingLatentMTexture = this.working3LatentMTexture;
            } else {
                //console.log('bindWorking unknown');
            }

            if (this.activeWorkingDepthTexture !== null) {
                gc.check('bwf5', ()=>GPURunner.activateTexture(gc, this.workingFrameTextureNumbers[2], this.activeWorkingDepthTexture));
            }
            if (this.activeWorkingLatentMTexture !== null) {
                gc.check('bwf6', ()=>GPURunner.activateTexture(gc, this.workingFrameTextureNumbers[1], this.activeWorkingLatentMTexture));
            }
            if (this.activeWorkingLatentCTexture !== null) {
                gc.check('bwf7', ()=>GPURunner.activateTexture(gc, this.workingFrameTextureNumbers[0], this.activeWorkingLatentCTexture));
            }
        }
    }

    bindOutputFrame(gc: GPUChecker, frame: WebGLFramebuffer | null = null, latentCTex: WebGLTexture | null = null, latentMTex: WebGLTexture | null = null, depthTex: WebGLTexture | null = null) {
        let gl = GPURunner.gl!;
        if (frame === null) {
            gl.bindFramebuffer(gl.FRAMEBUFFER, null);
            this.activeOutputLatentCTexture = null;
            this.activeOutputLatentMTexture = null;
            this.activeOutputDepthTexture = null;
            this.activeOutputFramebuffer = null;
            return;
        }
        this.activeOutputFramebuffer = frame;
        if (latentCTex === null) {
            if (this.activeOutputFramebuffer === this.working1Framebuffer) {
                this.activeOutputLatentCTexture = this.working1LatentCTexture;
            } else if (this.activeOutputFramebuffer === this.accFramebuffer) {
                this.activeOutputLatentCTexture = this.accLatentCTexture;
            } else if (this.activeOutputFramebuffer === this.working2Framebuffer) {
                this.activeOutputLatentCTexture = this.working2LatentCTexture;
            } else if (this.activeOutputFramebuffer === this.working3Framebuffer) {
                this.activeOutputLatentCTexture = this.working3LatentCTexture;
            } else {
                this.activeOutputLatentCTexture = null;
            }
        } else {
            this.activeOutputLatentCTexture = latentCTex;
        }
        if (latentMTex === null) {
            if (this.activeOutputFramebuffer === this.working1Framebuffer) {
                this.activeOutputLatentMTexture = this.working1LatentMTexture;
            } else if (this.activeOutputFramebuffer === this.accFramebuffer) {
                this.activeOutputLatentMTexture = this.accLatentMTexture;
            } else if (this.activeOutputFramebuffer === this.working2Framebuffer) {
                this.activeOutputLatentMTexture = this.working2LatentMTexture;
            } else if (this.activeOutputFramebuffer === this.working3Framebuffer) {
                this.activeOutputLatentMTexture = this.working3LatentMTexture;
            } else {
                this.activeOutputLatentMTexture = null;
            }
        } else {
            this.activeOutputLatentMTexture = latentMTex;
        }

        if (depthTex === null) {
            if (this.activeOutputFramebuffer === this.working1Framebuffer) {
                this.activeOutputDepthTexture = this.working1DepthTexture;
            } else if (this.activeOutputFramebuffer === this.accFramebuffer) {
                this.activeOutputDepthTexture = this.accDepthTexture;
            } else if (this.activeOutputFramebuffer === this.working2Framebuffer) {
                this.activeOutputDepthTexture = this.working2DepthTexture;
            } else if (this.activeOutputFramebuffer === this.working3Framebuffer) {
                this.activeOutputDepthTexture = this.working3DepthTexture;
            } else {
                this.activeOutputDepthTexture = null;
            }
        } else {
            this.activeOutputDepthTexture = depthTex;
        }

        gc.check('bof', ()=>gl.bindFramebuffer(gl.FRAMEBUFFER, this.activeOutputFramebuffer));

    }

    bindExternalOutputFrame(gcFrom: GPUChecker, fbTemp: WebGLFramebuffer, colorTex: WebGLTexture, depthTex: WebGLTexture | null) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'bindExternalOutputFrame');
        const gl = gc.gl;
        gc.checkAPI('1', ()=>gl.bindFramebuffer(gl.FRAMEBUFFER, fbTemp));
        gc.checkAPI('2', ()=>gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, colorTex, 0));
        if (depthTex !== null) {
            gc.checkAPI('3', ()=>gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT1, gl.TEXTURE_2D, depthTex, 0));
            gc.checkAPI('4', ()=>gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1]));
        } else {
            gc.checkAPI('5', ()=>gl.drawBuffers([gl.COLOR_ATTACHMENT0]));
        }

        this.activeOutputFramebuffer = fbTemp;
        this.activeOutputLatentCTexture = colorTex;
        this.activeOutputLatentMTexture = null;
        this.activeOutputDepthTexture = depthTex;
    }

    // importExternalTextures(textColor: WebGLTexture, textDepth: WebGLTexture | null) {
    //     let gl = GPURunner.gl!;
    //     let pi = TouchPageChange.externalToLatentProgram;
    //     gl.useProgram(pi.program);
    //     this.initializeStartupProgram(pi);
    //     this.bindOutputFrame(TouchPageChange.touchBuffers!.importedFramebuffer, TouchPageChange.touchBuffers!.importedLatentCTexture, TouchPageChange.touchBuffers!.importedLatentMTexture, TouchPageChange.touchBuffers!.importedDepthTexture);

    //     // set up the source color texture
    //     gl.activeTexture(gl.TEXTURE0 + TouchPageChange.externalColorTexNumVal);
    //     gl.bindTexture(gl.TEXTURE_2D, textColor);
    //     if (textDepth !== null) {
    //         gl.activeTexture(gl.TEXTURE0 + TouchPageChange.externalDepthTexNumVal);
    //         gl.bindTexture(gl.TEXTURE_2D, textDepth);
    //     } else {
    //         gl.activeTexture(gl.TEXTURE0 + TouchPageChange.externalDepthTexNumVal);
    //         gl.bindTexture(gl.TEXTURE_2D, TouchPageChange.touchBuffers!.nullDepthTexture);
    //     }
    //     gl.drawArrays(gl.TRIANGLES, 0, PageChange.verts);

    // }

    stashOutput() {
        this.activeStashedLatentCTexture = this.activeOutputLatentCTexture;
        this.activeStashedLatentMTexture = this.activeOutputLatentMTexture;
        this.activeStashedDepthTexture = this.activeOutputDepthTexture;
        this.activeStashedFramebuffer = this.activeOutputFramebuffer;
    }

    bindStashAsWorking(gc: GPUChecker) {
        this.bindWorkingFrame(gc, this.activeStashedFramebuffer, this.activeStashedLatentCTexture, this.activeStashedLatentMTexture, this.activeStashedDepthTexture);
        this.activeStashedFramebuffer = this.activeStashedLatentCTexture = this.activeStashedLatentMTexture = this.activeStashedDepthTexture = null;
    }

    preserveOutput() {
        if (this.activePreservedLatentCTexture !== null) {
            console.log('preserved output already set');
        }
        this.activePreservedLatentCTexture = this.activeOutputLatentCTexture;
        this.activePreservedLatentMTexture = this.activeOutputLatentMTexture;
        this.activePreservedDepthTexture = this.activeOutputDepthTexture;
        this.activePreservedFramebuffer = this.activeOutputFramebuffer;
    }

    bindPreservedAsWorking(gc: GPUChecker) {
        this.bindWorkingFrame(gc, this.activePreservedFramebuffer, this.activePreservedLatentCTexture, this.activePreservedLatentMTexture, this.activePreservedDepthTexture);
        this.activePreservedFramebuffer = this.activePreservedLatentCTexture = this.activePreservedLatentMTexture = this.activePreservedDepthTexture = null;
    }

    directOutputTowardAccumulator(gcFrom: GPUChecker) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'directOutputTowardAccumulator');
        const gl = gc.gl;
        let buffers = TouchPageChange.touchBuffers!;
        //console.log('bindOutputIntoAccumulator');

        // no accumulator because we write to the accumulator
        gc.check('1', ()=>GPURunner.activateTexture(gc, TouchPageChange.accumWetDepthTexNumVal, null));
        gc.check('2', ()=>GPURunner.activateTexture(gc, TouchPageChange.accumLatentCTexNumVal, null));
        gc.check('3', ()=>GPURunner.activateTexture(gc, TouchPageChange.accumLatentMTexNumVal, null));
        
        gc.check('4', ()=>this.bindOutputFrame(gc, this.accFramebuffer, this.accLatentCTexture, this.accLatentMTexture, this.accDepthTexture));
        this.activeAccFramebuffer = null;
        this.activeAccLatentCTexture = null;
        this.activeAccLatentMTexture = null;
        this.activeAccDepthTexture = null;
    }

    useTempAccumulator(gcFrom: GPUChecker, frame: WebGLFramebuffer | null = null, latentCTex: WebGLTexture | null = null, latentMTex: WebGLTexture | null = null, depthTex: WebGLTexture | null = null) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'useTempAccumulator');
        const gl = gc.gl;

        this.activeAccFramebuffer = frame;
        this.activeAccLatentCTexture = latentCTex;
        this.activeAccLatentMTexture = latentMTex;
        this.activeAccDepthTexture = depthTex;

        gc.check('1', ()=>GPURunner.activateTexture(gc, TouchPageChange.accumWetDepthTexNumVal, this.activeAccDepthTexture));
        gc.check('2', ()=>GPURunner.activateTexture(gc, TouchPageChange.accumLatentMTexNumVal, this.activeAccLatentMTexture));
        gc.check('3', ()=>GPURunner.activateTexture(gc, TouchPageChange.accumLatentCTexNumVal, this.activeAccLatentCTexture));
    }
    useAccumulator(gcFrom: GPUChecker) {
        let buffers = TouchPageChange.touchBuffers!;
        this.useTempAccumulator(gcFrom, this.accFramebuffer, this.accLatentCTexture, this.accLatentMTexture, this.accDepthTexture);
    }

    useAvailableOutputFrame(gcFrom: GPUChecker) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'useAvailableOutputFrame');
        let buffers = TouchPageChange.touchBuffers!;
        // use either frame1 or frame2 as the output.
        // this assumes that one of those is not the stash, working or accumulator
        if (this.working1LatentCTexture !== this.activeWorkingLatentCTexture && this.working1LatentCTexture !== this.activeAccLatentCTexture && this.working1LatentCTexture !== this.activeStashedLatentCTexture && this.working1LatentCTexture !== this.activePreservedLatentCTexture) {
            gc.check('', ()=>this.bindOutputFrame(gc, this.working1Framebuffer));
        } else if (this.working2LatentCTexture !== this.activeWorkingLatentCTexture && this.working2LatentCTexture !== this.activeAccLatentCTexture && this.working2LatentCTexture !== this.activeStashedLatentCTexture && this.working2LatentCTexture !== this.activePreservedLatentCTexture) {
            gc.check('', ()=>this.bindOutputFrame(gc, this.working2Framebuffer));
        } else if (this.working3LatentCTexture !== this.activeWorkingLatentCTexture && this.working3LatentCTexture !== this.activeAccLatentCTexture && this.working3LatentCTexture !== this.activeStashedLatentCTexture && this.working3LatentCTexture !== this.activePreservedLatentCTexture) {
            gc.check('', ()=>this.bindOutputFrame(gc, this.working3Framebuffer));
        } else {
            console.log('no available output frame');
        }
    }

    usePageReference(gcFrom: GPUChecker, latentCTex: WebGLTexture | null = null, latentMTex: WebGLTexture | null = null, wetDepthTex: WebGLTexture | null = null, dryDepthTex: WebGLTexture | null = null) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'usePageReference');
        const gl = gc.gl;

        gc.check('1', ()=>GPURunner.activateTexture(gc, TouchPageChange.referenceDryDepthTexNumVal, dryDepthTex));
        gc.check('2', ()=>GPURunner.activateTexture(gc, TouchPageChange.referenceWetDepthTexNumVal, wetDepthTex));
        gc.check('3', ()=>GPURunner.activateTexture(gc, TouchPageChange.referenceLatentMTexNumVal, latentMTex));
        gc.check('4', ()=>GPURunner.activateTexture(gc, TouchPageChange.referenceLatentCTexNumVal, latentCTex));
    }


    //static setviewportcount = 0;

    override setViewport(): void {
        // set the resolution
        let gl = GPURunner.gl!;
        let width = this.width;
        let height = this.height;
        if (gl.canvas.width !== width || gl.canvas.height !== height) {
            gl.canvas.width = width;
            gl.canvas.height = height;
            gl.viewport(0, 0, width, height);
            // TouchPageChange.setviewportcount ++;
            // if (TouchPageChange.setviewportcount % 10 === 0) {
            //     console.log('setviewportcount', TouchPageChange.setviewportcount);
            // }
        }
    }

    override preparePageForStartup(gcFrom: GPUChecker): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'touch-pass');
        GPURunner.initTouchWetPageChange(gc, this.page, this);
        //console.log('preparePageForStartup');
        //this.debugSpewDepthTexture(this.pageWetDepthTexture!);

        //GPURunner.initTouchVisiblePageChange(this.page, this);

    }
    override applyStartupToPage(gcFrom: GPUChecker): void {
    }
    override preparePageForProcess(gcFrom: GPUChecker): void {
    }
    override applyProcessToPage(gcFrom: GPUChecker): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'touch-pass');
        GPURunner.applyTouchWetPageChange(gc, this.page, this);
    }
    override preparePageForFinish(gcFrom: GPUChecker): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'touch-pass');
        GPURunner.initTouchVisiblePageChange(gc, this.page, this);
    }
    override applyFinishToPage(gcFrom: GPUChecker): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'touch-pass');
        //GPURunner.applyTouchWetPageChange(this.page, this);
        GPURunner.applyTouchVisiblePageChange(gc, this.page, this);

    }


    override selectStartupProgram(gcFrom: GPUChecker): TouchProgramInfo {
        let gl = GPURunner.gl!;
        let pi = TouchPageChange.externalToLatentProgram;

        gcFrom.checkAPI('touch-ssp', ()=>gl.useProgram(pi.program));
        return pi;
    }

    override initializeStartupProgram(gcFrom: GPUChecker, pi: TouchProgramInfo) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'touch-pass');
        let buffers = TouchPageChange.touchBuffers!;
        this._initializeStartupProgram(gc, pi, buffers);
    }

    _initializeSharedProgram(gcFrom: GPUChecker, pi: TouchProgramInfo, buffers: TouchProgramBuffers): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'touch-pass');
        const gl = gc.gl;
        
        if (GPURunner.doInitializeProgram(pi, 'touch-shared')) {
            gl.uniform1i(pi.uniformLocations.kmLookupTexNum, TouchPageChange.kmLookupTexNumVal);
            gl.uniform1i(pi.uniformLocations.workingLatentCTexNum, TouchPageChange.workingLatentCTexNumVal);
            gl.uniform1i(pi.uniformLocations.workingLatentMTexNum, TouchPageChange.workingLatentMTexNumVal);
            gl.uniform1i(pi.uniformLocations.workingWetDepthTexNum, TouchPageChange.workingWetDepthTexNumVal);
            gl.uniform1i(pi.uniformLocations.workingDryDepthTexNum, TouchPageChange.workingDryDepthTexNumVal);
            gl.uniform1i(pi.uniformLocations.accumLatentCTexNum, TouchPageChange.accumLatentCTexNumVal);
            gl.uniform1i(pi.uniformLocations.accumLatentMTexNum, TouchPageChange.accumLatentMTexNumVal);
            gl.uniform1i(pi.uniformLocations.accumWetDepthTexNum, TouchPageChange.accumWetDepthTexNumVal);
            gl.uniform1i(pi.uniformLocations.accumDryDepthTexNum, TouchPageChange.accumDryDepthTexNumVal);
            gl.uniform1i(pi.uniformLocations.referenceLatentCTexNum, TouchPageChange.referenceLatentCTexNumVal);
            gl.uniform1i(pi.uniformLocations.referenceLatentMTexNum, TouchPageChange.referenceLatentMTexNumVal);
            gl.uniform1i(pi.uniformLocations.referenceWetDepthTexNum, TouchPageChange.referenceWetDepthTexNumVal);
            gl.uniform1i(pi.uniformLocations.referenceDryDepthTexNum, TouchPageChange.referenceDryDepthTexNumVal);

        }
   
    }


    _initializeStartupProgram(gcFrom: GPUChecker, pi: TouchProgramInfo, buffers: TouchProgramBuffers) {
        let gl = GPURunner.gl!;

        if (GPURunner.doInitializeProgram(pi, 'touch')) {
            gl.uniform1i(pi.uniformLocations.kmLookupTexNum, TouchPageChange.kmLookupTexNumVal);
            //gl.uniform1i(pi.uniformLocations.externalColorTexNum, TouchPageChange.externalColorTexNumVal);
            //gl.uniform1i(pi.uniformLocations.externalDepthTexNum, TouchPageChange.externalDepthTexNumVal);
        }

    }

    override specializeStartupRoutine(gcFrom: GPUChecker, pi: TouchProgramInfo) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'touch-pass');
        let buffers = TouchPageChange.touchBuffers!;
        this._specializeStartupRoutine(gc, pi, buffers);
    }

    _specializeStartupRoutine(gcFrom: GPUChecker, pi: TouchProgramInfo, buffers: TouchProgramBuffers) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, '_specializeStartupRoutine');
        const gl = gc.gl;
        
        // set the resolution
        gl.canvas.width = this.width;
        gl.canvas.height = this.height;

        // set up the working textures
        let working = gc.check('1', ()=>GPURunner.workingFrameFactory.getItem(gc, undefined, this.width, this.height, [
            {tag:'initial-latC', internalFormat:gl.RGBA16UI, format: gl.RGBA_INTEGER, type:gl.UNSIGNED_SHORT},
            {tag:'initial-latM', internalFormat:gl.RGBA16UI, format: gl.RGBA_INTEGER, type:gl.UNSIGNED_SHORT},
            {tag:'initial-wetD', internalFormat:gl.R16UI, format: gl.RED_INTEGER, type:gl.UNSIGNED_SHORT},
            {tag:'initial-wetA', internalFormat:gl.R8UI, format: gl.RED_INTEGER, type:gl.UNSIGNED_BYTE}
        ])) as WorkingItem;

        this.initialWetLatentCTexture = working.tex0;
        this.initialWetLatentMTexture = working.tex1;
        this.initialWetDepthTexture = working.tex2;
        this.initialWetAgeTexture = working.tex3;

        working = gc.check('2', ()=>GPURunner.workingFrameFactory.getItem(gc, 'working1', this.width, this.height, [
            {tag:'latC', internalFormat:gl.RGBA16UI, format: gl.RGBA_INTEGER, type:gl.UNSIGNED_SHORT},
            {tag:'latM', internalFormat:gl.RGBA16UI, format: gl.RGBA_INTEGER, type:gl.UNSIGNED_SHORT},
            {tag:'depth', internalFormat:gl.R16UI, format: gl.RED_INTEGER, type:gl.UNSIGNED_SHORT}
        ])) as WorkingItem;

        this.working1Framebuffer = working.fb;
        this.working1LatentCTexture = working.tex0;
        this.working1LatentMTexture = working.tex1;
        this.working1DepthTexture = working.tex2;

        working = gc.check('3', ()=>GPURunner.workingFrameFactory.getItem(gc, 'working2', this.width, this.height, [
            {tag:'latC', internalFormat:gl.RGBA16UI, format: gl.RGBA_INTEGER, type:gl.UNSIGNED_SHORT},
            {tag:'latM', internalFormat:gl.RGBA16UI, format: gl.RGBA_INTEGER, type:gl.UNSIGNED_SHORT},
            {tag:'depth', internalFormat:gl.R16UI, format: gl.RED_INTEGER, type:gl.UNSIGNED_SHORT}
        ])) as WorkingItem;

        this.working2Framebuffer = working.fb;
        this.working2LatentCTexture = working.tex0;
        this.working2LatentMTexture = working.tex1;
        this.working2DepthTexture = working.tex2;
   
        working = gc.check('4', ()=>GPURunner.workingFrameFactory.getItem(gc, 'working3', this.width, this.height, [
            {tag:'latC', internalFormat:gl.RGBA16UI, format: gl.RGBA_INTEGER, type:gl.UNSIGNED_SHORT},
            {tag:'latM', internalFormat:gl.RGBA16UI, format: gl.RGBA_INTEGER, type:gl.UNSIGNED_SHORT},
            {tag:'depth', internalFormat:gl.R16UI, format: gl.RED_INTEGER, type:gl.UNSIGNED_SHORT}
        ])) as WorkingItem;

        this.working3Framebuffer = working.fb;
        this.working3LatentCTexture = working.tex0;
        this.working3LatentMTexture = working.tex1;
        this.working3DepthTexture = working.tex2;

        working = gc.check('4', ()=>GPURunner.workingFrameFactory.getItem(gc, 'acc', this.width, this.height, [
            {tag:'latC', internalFormat:gl.RGBA16UI, format: gl.RGBA_INTEGER, type:gl.UNSIGNED_SHORT},
            {tag:'latM', internalFormat:gl.RGBA16UI, format: gl.RGBA_INTEGER, type:gl.UNSIGNED_SHORT},
            {tag:'depth', internalFormat:gl.R16UI, format: gl.RED_INTEGER, type:gl.UNSIGNED_SHORT}
        ])) as WorkingItem;

        this.accFramebuffer = working.fb;
        this.accLatentCTexture = working.tex0;
        this.accLatentMTexture = working.tex1;
        this.accDepthTexture = working.tex2;
    }

    override runStartupRoutine(gcFrom: GPUChecker, pi: TouchProgramInfo): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'touch-pass');
        let buffers = TouchPageChange.touchBuffers!;
        this._runStartupRoutine(gc, pi, buffers);

    }
    _runStartupRoutine(gcFrom: GPUChecker, pi: TouchProgramInfo, buffers: TouchProgramBuffers): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, '_runStartupRoutine');
        const gl = gc.gl;

        // stash the starting wet paint
        const fbTemp1 = gc.checkAPI('1', ()=>gl.createFramebuffer(), true) as WebGLFramebuffer;
        gc.checkAPI('2', ()=>gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbTemp1));
        gc.checkAPI('3', ()=>gl.framebufferTexture2D(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.pageWetLatentCTexture, 0));
        const fbTemp2 = gc.checkAPI('4', ()=>gl.createFramebuffer(), true) as WebGLFramebuffer;
        gc.checkAPI('4', ()=>gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, fbTemp2));
        gc.checkAPI('5', ()=>gl.framebufferTexture2D(gl.DRAW_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.initialWetLatentCTexture, 0));
        gc.checkAPI('6', ()=>gl.blitFramebuffer(0, 0, this.width, this.height, 0, 0, this.width, this.height, gl.COLOR_BUFFER_BIT, gl.NEAREST));

        gc.checkAPI('8', ()=>gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbTemp1));
        gc.checkAPI('9', ()=>gl.framebufferTexture2D(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.pageWetLatentMTexture, 0));
        gc.checkAPI('10', ()=>gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, fbTemp2));
        gc.checkAPI('11', ()=>gl.framebufferTexture2D(gl.DRAW_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.initialWetLatentMTexture, 0));
        gc.checkAPI('12', ()=>gl.blitFramebuffer(0, 0, this.width, this.height, 0, 0, this.width, this.height, gl.COLOR_BUFFER_BIT, gl.NEAREST));

        gc.checkAPI('13', ()=>gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbTemp1));
        gc.checkAPI('14', ()=>gl.framebufferTexture2D(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.pageWetDepthTexture, 0));
        gc.checkAPI('15', ()=>gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, fbTemp2));
        gc.checkAPI('16', ()=>gl.framebufferTexture2D(gl.DRAW_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.initialWetDepthTexture, 0));
        gc.checkAPI('17', ()=>gl.blitFramebuffer(0, 0, this.width, this.height, 0, 0, this.width, this.height, gl.COLOR_BUFFER_BIT, gl.NEAREST));

        gc.checkAPI('18', ()=>gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbTemp1));
        gc.checkAPI('19', ()=>gl.framebufferTexture2D(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.pageWetAgeTexture, 0));
        gc.checkAPI('20', ()=>gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, fbTemp2));
        gc.checkAPI('21', ()=>gl.framebufferTexture2D(gl.DRAW_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.initialWetAgeTexture, 0));
        gc.checkAPI('22', ()=>gl.blitFramebuffer(0, 0, this.width, this.height, 0, 0, this.width, this.height, gl.COLOR_BUFFER_BIT, gl.NEAREST));

        this.activeOutputDepthTexture = this.initialWetDepthTexture;
        this.activeOutputLatentCTexture = this.initialWetLatentCTexture;
        this.activeOutputLatentMTexture = this.initialWetLatentMTexture;
       
        gl.invalidateFramebuffer(gl.READ_FRAMEBUFFER, []);
        gl.deleteFramebuffer(fbTemp1);
    }

    override finalizeStartup(gcFrom: GPUChecker, pi: TouchProgramInfo): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'touch-pass');
        let buffers = TouchPageChange.touchBuffers!;
        this._finalizeStartup(gc, pi, buffers);

    }
    _finalizeStartup(gcFrom: GPUChecker, pi: TouchProgramInfo, buffers: TouchProgramBuffers): void {

    }

    override selectProcessProgram(gcFrom: GPUChecker): TouchProgramInfo {
        let gl = GPURunner.gl!;
        let pi = TouchPageChange.processAccumulateTakesProgram;

        gcFrom.checkAPI('touch-spp', ()=>gl.useProgram(pi.program));
        return pi;
    }

    override initializeProcessProgram(gcFrom: GPUChecker, pi: TouchProgramInfo): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'touch-pass');
        let buffers = TouchPageChange.touchBuffers!;
        this._initializeProcessProgram(gc, pi, buffers);

    }

    _initializeProcessProgram(gcFrom: GPUChecker, pi: TouchProgramInfo, buffers: TouchProgramBuffers): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, '_initializeProcessProgram');
        const gl = gc.gl;

        gc.check('1', ()=>this._initializeSharedProgram(gc, pi, buffers));
        if (GPURunner.doInitializeProgram(pi, 'touch')) {
            gl.uniform1i(pi.uniformLocations.actionFromTexNum, TouchPageChange.actionFromTexNumVal);
            gl.uniform1i(pi.uniformLocations.actionFracTexNum, TouchPageChange.actionFracTexNumVal);
            gl.uniform1i(pi.uniformLocations.actionCommitTexNum, TouchPageChange.actionCommitTexNumVal);
            gl.uniform1i(pi.uniformLocations.maxDepthTexNum, TouchPageChange.maxDepthTexNumVal);
        }
   
    }
    override specializeProcessRoutine(gcFrom: GPUChecker, pi: TouchProgramInfo): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'touch-pass');
        let buffers = TouchPageChange.touchBuffers!;
        this._specializeProcessRoutine(gc, pi, buffers);
    }

    _specializeProcessRoutine(gcFrom: GPUChecker, pi: TouchProgramInfo, buffers: TouchProgramBuffers): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, '_specializeProcessRoutine');
        const gl = gc.gl;

        let working = gc.check('1', ()=>GPURunner.workingFrameFactory.getItem(gc, undefined, this.width, this.height, [
            {tag:'finalWetAge', internalFormat:gl.R8UI, format: gl.RED_INTEGER, type:gl.UNSIGNED_BYTE}
        ])) as WorkingItem;
        this.finalWetAgeTexture = working.tex0;
   
    }

    override runProcessRoutine(gcFrom: GPUChecker, pi: TouchProgramInfo): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'touch-pass');
        let buffers = TouchPageChange.touchBuffers!;
        this._runProcessRoutine(gc, pi, buffers);
    }
    _runProcessRoutine(gcFrom: GPUChecker, pi: TouchProgramInfo, buffers: TouchProgramBuffers): void {
    }

    accumulateForEntries(gcFrom: GPUChecker, perEntryDo: (tipLoc: PageChangeEntry)=>boolean): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'accumulateForEntries');
        const gl = gc.gl;
        
        // start with accumulator set
        gc.check('1', ()=>this.useAccumulator(gc));
        for (const tipLoc of this.entries) {
            if (perEntryDo(tipLoc) === false) {
                break;
            }
        }
    }

    accumulateTextures(gcFrom: GPUChecker, center: Point2D, textures: ChangeTextureIndexes, idxFirst:number, idxLast:number, getAccumulateProgram: (idx: number)=>TouchProgramInfo, getCommitProgram: (idx: number, stopEarly: boolean)=>TouchProgramInfo): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'accumulateTextures');
        const gl = gc.gl;
        
        let offsetX = center.x - (textures.width)/2;
        let offsetY = center.y - (textures.height)/2;

        for (let idx=idxFirst; idx < idxLast; idx++) {
            let stopNow = false;
            let pi = getAccumulateProgram(idx - idxFirst);
            if ((idx - idxFirst) === 0) {
                gc.check('1', ()=>this.initializeProcessProgram(gc, pi));
                gc.checkAPI('2', ()=>gl.uniform2i(pi.uniformLocations.maxDepthTextureOffset, offsetX, offsetY));
                gc.checkAPI('3', ()=>gl.uniform2i(pi.uniformLocations.actionTextureOffset, offsetX, offsetY));
                gc.checkAPI('4', ()=>gl.uniform1i(pi.uniformLocations.maxDepthTextureIndex, textures.idxMax));
                gc.checkAPI('5', ()=>gl.uniform1i(pi.uniformLocations.writeAccum, 1));
            }
            gc.checkAPI('6', ()=>gl.uniform1i(pi.uniformLocations.actionTextureIndex, textures.idxsAction[idx]));

            this.stashOutput();
            gc.check('7', ()=>this.directOutputTowardAccumulator(gc));
            gc.check('8', ()=>this.bindStashAsWorking(gc));
            //console.log('depth into accumulate')   
            //this.debugSpewDepthTexture(this.workingDepthTexture!)


            gc.checkFB('pre9');
            gc.checkAPI('9', ()=>gl.drawArrays(gl.TRIANGLES, 0, PageChange.verts));

            //console.log('accumulator')   
            //this.debugSpewDepthTexture(this.outputDepthTexture!)

            //this.debugSpewDepthTexture(this.outputDepthTexture!)
            //console.log('acum', this.debugMeasureDepthTexture(this.outputDepthTexture!));

            // peek ahead to see if we need to avoid writing to the accumulator and stop now
            stopNow = idx+1 < textures.idxsAction.length && textures.idxsAction[idx + 1] === -1;

            pi = getCommitProgram((idx - idxFirst), stopNow);
            if ((idx - idxFirst) === 0) {
                gc.check('10', ()=>this.initializeProcessProgram(gc, pi));
                gc.checkAPI('11', ()=>gl.uniform2i(pi.uniformLocations.maxDepthTextureOffset, offsetX, offsetY));
                gc.checkAPI('12', ()=>gl.uniform2i(pi.uniformLocations.actionTextureOffset, offsetX, offsetY));
                gc.checkAPI('13', ()=>gl.uniform1i(pi.uniformLocations.maxDepthTextureIndex, textures.idxMax));
                gc.checkAPI('14', ()=>gl.uniform1i(pi.uniformLocations.writeAccum, 1));
            }
            gc.checkAPI('15', ()=>gl.uniform1i(pi.uniformLocations.actionTextureIndex, textures.idxsAction[idx]));

            // make the output the frame that isn't the accumulator or the input
            gc.check('16', ()=>this.useAvailableOutputFrame(gc));
            // use that accumulator
            gc.check('17', ()=>this.useAccumulator(gc));

            //gl.uniform1i(pi.uniformLocations.debug, 1);


            gc.checkFB('pre18');
            gc.checkAPI('18', ()=>gl.drawArrays(gl.TRIANGLES, 0, PageChange.verts));
            //console.log('new depth')   
            //this.debugSpewDepthTexture(this.outputDepthTexture!)
            //console.log('new depth', this.debugMeasureDepthTexture(this.outputDepthTexture!)[0])

            if (stopNow) {
                break;
            }   
        }

    }

    override finalizeProcess(gcFrom: GPUChecker, pi: TouchProgramInfo): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'touch-pass');
        let buffers = TouchPageChange.touchBuffers!;
        this._finalizeProcess(gc, pi, buffers);
    }

    static anglePushes: Array<number>;
    _finalizeProcess(gcFrom: GPUChecker, pi: TouchProgramInfo, buffers: TouchProgramBuffers): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, '_finalizeProcess');
        const gl = gc.gl;


        if (this.centralPull !== undefined) {
            let buff0123 = new Uint16Array(this.width * this.height * 4);
            let buff5678 = new Uint16Array(this.width * this.height * 4);
            let buffSelf = new Uint16Array(this.width * this.height);

            if (TouchPageChange.anglePushes === undefined) {
                // once around a circle about 55 units out 
                // go in 4 sections with 80 angles in each. push percentages range ove the 80
                TouchPageChange.anglePushes = new Array<number>(9 * 80 * 4);
                let quadParams = [{memberStart: 3, memberMid: 6, memberEnd: 7},
                    {memberStart: 7, memberMid: 8, memberEnd: 5},
                    {memberStart: 5, memberMid: 2, memberEnd: 1},
                    {memberStart: 1, memberMid: 0, memberEnd: 3}
                ];
                let aiOff = 0;
                for (let q=0; q < 4; q++) {
                    let quad = quadParams[q];
                
                    for (let ai = 0; ai < 80; ai++) {
                        let pctStart = (80.0 - 1 - ai) / 80.0;
                        let pctEnd = ai / 80.0;
                        let pctMid = ai < 40 ? ai / 40.0 : (40 - 1 - (ai-40)) / 40.0;
                        // normalize these to 0-1. this turns this linear interpolation into more of a circle effect
                        let total = pctStart + pctMid + pctEnd;
                        pctStart /= total;
                        pctMid /= total;
                        pctEnd /= total;
                        let scaleStart = Math.round(pctStart * 60000.0);
                        let scaleMid = Math.round(pctMid * 60000.0);
                        let scaleEnd = Math.round(pctEnd * 60000.0);
                        if (total > 0) {
                            let scaleGap = 60000.0 - (scaleStart + scaleMid + scaleEnd);
                            if (scaleGap > 0) {
                                let scaleMax = Math.max(scaleStart, scaleMid, scaleEnd);
                                if (scaleMax === scaleStart) {
                                    scaleStart += scaleGap;
                                } else if (scaleMax === scaleMid) {
                                    scaleMid += scaleGap;
                                } else {
                                    scaleEnd += scaleGap;
                                }
                            } else if (scaleGap < 0) {
                                let scaleMin = Math.min(scaleStart === 0 ? 2 : scaleStart, scaleMid === 0 ? 2 : scaleMid, scaleEnd === 0 ? 2 : scaleEnd);
                                if (scaleMin === scaleStart) {
                                    scaleStart += scaleGap;
                                } else if (scaleMin === scaleMid) {
                                    scaleMid += scaleGap;
                                } else {
                                    scaleEnd += scaleGap;
                                }

                            }
                        }

                        TouchPageChange.anglePushes[aiOff * 9 + quad.memberStart] = scaleStart;
                        TouchPageChange.anglePushes[aiOff * 9 + quad.memberMid] = scaleMid;
                        TouchPageChange.anglePushes[aiOff * 9 + quad.memberEnd] = scaleEnd;
                        aiOff ++;
                    }
                }
            }
            const buffOff = (x:number, y:number):number => {
                //return (( this.height - 1 - y) * this.width + x);
                return ((  y) * this.width + x);
            }


            let ptCenter = new Point2D(this.page.cellsWidth/2, this.page.cellsHeight/2);
            let pageFirstY = this.page.cellsHeight - 1 - (this.height + this.pageY);
            const aiFromPageOff = (dx: number, dy:number): number => {
                let ptLocate = new Point2D(this.pageX +dx, pageFirstY + dy);
                let aLocate = ptCenter.angleToward(ptLocate)!;
                let aiLocate = Math.floor((aLocate >= 0 ? aLocate : aLocate + Math.PI * 2) / (Math.PI * 2) * 80*4);
                return aiLocate * 9;
            }
            for (let y = 0; y < this.height; y++) {
                for (let x = 0; x < this.width; x++) {
                    let offset = buffOff(x,y);
                    let ptPage = new Point2D(this.pageX + x, pageFirstY + y);
                    let d = ptCenter.distanceTo(ptPage);
                    let c = Math.min(1.0, this.centralPull(d));
                    buffSelf[offset] = Math.round(c * 60000);

                    //let pushes = TouchPageChange.anglePushes.slice(ai * 9, ai * 9 + 9);
                    // if (x > this.width/2 - 1 && x < this.width/2 + 1) {
                    //     console.log('centerish', ptPage.x, ptPage.y, ptCenter.x, a, ai, pushes[7], pushes[1]);
                    // }
                    
                    if (x > 0 && y < this.height - 1) {
                        let aiPusher = aiFromPageOff(x-1, y+1);
                        buff0123[offset*4 + 0] = TouchPageChange.anglePushes[aiPusher + 8];
                    }
                    if (y < this.height - 1) {
                        let aiPusher = aiFromPageOff(x, y+1);
                        buff0123[offset*4 + 1] = TouchPageChange.anglePushes[aiPusher + 7];
                    }
                    if (x < this.width - 1 && y < this.height - 1) {
                        let aiPusher = aiFromPageOff(x+1, y+1);
                        buff0123[offset*4 + 2] = TouchPageChange.anglePushes[aiPusher + 6];
                    }
                    if (x > 0) {
                        let aiPusher = aiFromPageOff(x-1, y);
                        buff0123[offset*4 + 3] = TouchPageChange.anglePushes[aiPusher + 5];
                    }
                    if (x < this.width - 1) {
                        let aiPusher = aiFromPageOff(x+1, y);
                        buff5678[offset*4 + 0] = TouchPageChange.anglePushes[aiPusher + 3];
                    }
                    if (x > 0 && y > 0) {
                        let aiPusher = aiFromPageOff(x-1, y-1);
                        buff5678[offset*4 + 1] = TouchPageChange.anglePushes[aiPusher + 2];
                    }   
                    if (y > 0) {
                        let aiPusher = aiFromPageOff(x, y-1);
                        buff5678[offset*4 + 2] = TouchPageChange.anglePushes[aiPusher + 1];
                    }
                    if (x < this.width - 1 && y > 0) {
                        let aiPusher = aiFromPageOff(x+1, y-1);
                        buff5678[offset*4 + 3] = TouchPageChange.anglePushes[aiPusher + 0];
                    }
                   
                }

            }

            // console.log('buffs');
            // for (let y = this.height - 1; y >= 0; y--) {
            //     let spew = '';
            //     // for (let x = 0; x < this.width; x++) {
            //     //     let offset = y*this.width + x;
            //     //     // if (offset == 54) {
            //     //     //     console.log('offset 54', buff0123[54*4 + 0], buff0123[54*4 + 1], buff0123[54*4 + 2], buff0123[54*4 + 3]);
            //     //     // }
            //     //     let s = buffSelf[offset]/60000;
            //     //     let si = Math.floor(s * 100);
            //     //     let p0 = buff0123[offset*4 + 0] / 60000;
            //     //     let p0i = Math.floor(p0 * 100);
            //     //     let p1 = buff0123[offset*4 + 1] / 60000;
            //     //     let p1i = Math.floor(p1 * 100);
            //     //     let p2 = buff0123[offset*4 + 2] / 60000;
            //     //     let p2i = Math.floor(p2 * 100);
            //     //     let p3 = buff0123[offset*4 + 3] / 60000;
            //     //     let p3i = Math.floor(p3 * 100);
            //     //     let p5 = buff5678[offset*4 + 0] / 60000;
            //     //     let p5i = Math.floor(p5 * 100);
            //     //     let p6 = buff5678[offset*4 + 1] / 60000;
            //     //     let p6i = Math.floor(p6 * 100);
            //     //     let p7 = buff5678[offset*4 + 2] / 60000;
            //     //     let p7i = Math.floor(p7 * 100);
            //     //     let p8 = buff5678[offset*4 + 3] / 60000;
            //     //     let p8i = Math.floor(p8 * 100);
            //     //     spew += '[' +si.toString().padStart(3) + p0i.toString().padStart(3) + p1i.toString().padStart(3) + p2i.toString().padStart(3) + p3i.toString().padStart(3) + p5i.toString().padStart(3) + p6i.toString().padStart(3) + p7i.toString().padStart(3) + p8i.toString().padStart(3) + '] ';
            //     //     //spew += p0.toString().padStart(3) + ' ';
            //     //     //spew += s.toString().padStart(2)+'='+p0.toString().padStart(2) + '-' + p1.toString().padStart(2) + '-' + p2.toString().padStart(2) + ',';
            //     //     //spew += `[${si}=${p0i}-${p1i}-${p2i},] `;
            //     //     //spew += `[${si}=${p1i}] `;
            //     //     //spew += `${s},${p0},${p1},${p2},`;
                   
            //     // }
            //     for (let x = 0; x < this.width; x++) {
            //         let offset = y*this.width + x;
            //         let s = buffSelf[offset]/60000;
            //         let si = Math.floor(s * 100);
            //         spew += `${s},`;
            //     }
            //     spew += '99,';
            //     for (let x = 0; x < this.width; x++) {
            //         let offset = y*this.width + x;
            //         let p0 = buff0123[offset*4 + 0] / 60000;
            //         let p0i = Math.floor(p0 * 100);
            //         spew += `${p0},`;
            //     }
            //     spew += '99,';
            //     for (let x = 0; x < this.width; x++) {
            //         let offset = y*this.width + x;
            //         let p1 = buff0123[offset*4 + 1] / 60000;
            //         let p1i = Math.floor(p1 * 100);
            //         spew += `${p1},`;
            //     }
            //     spew += '99,';
            //     for (let x = 0; x < this.width; x++) {
            //         let offset = y*this.width + x;
            //         let p2 = buff0123[offset*4 + 2] / 60000;
            //         let p2i = Math.floor(p2 * 100);
            //         spew += `${p2},`;
            //     }
            //     spew += '99,';
            //     for (let x = 0; x < this.width; x++) {
            //         let offset = y*this.width + x;
            //         let p3 = buff0123[offset*4 + 3] / 60000;
            //         let p3i = Math.floor(p3 * 100);
            //         spew += `${p3},`;
            //     }
            //     spew += '99,';
            //     for (let x = 0; x < this.width; x++) {
            //         let offset = y*this.width + x;
            //         let p5 = buff5678[offset*4 + 0] / 60000;
            //         let p5i = Math.floor(p5 * 100);
            //         spew += `${p5},`;
            //     }
            //     spew += '99,';                
            //     for (let x = 0; x < this.width; x++) {
            //         let offset = y*this.width + x;
            //         let p6 = buff5678[offset*4 + 1] / 60000;
            //         let p6i = Math.floor(p6 * 100);
            //         spew += `${p6},`;
            //     }
            //     spew += '99,';                
            //     for (let x = 0; x < this.width; x++) {
            //         let offset = y*this.width + x;
            //         let p7 = buff5678[offset*4 + 2] / 60000;
            //         let p7i = Math.floor(p7 * 100);
            //         spew += `${p7},`;
            //     }
            //     spew += '99,';                
            //     for (let x = 0; x < this.width; x++) {
            //         let offset = y*this.width + x;
            //         let p8 = buff5678[offset*4 + 3] / 60000;
            //         let p8i = Math.floor(p8 * 100);
            //         spew += `${p8},`;
            //     }
            //     console.log(spew);

            // }

            //let d1 = this.debugMeasureDepthTexture(this.outputDepthTexture!, false)[0];
            //this.debugSpewDepthTexture(this.outputDepthTexture!);

            pi = TouchPageChange.centralPullWetChangesProgram;
            gc.checkAPI('1', ()=>gl.useProgram(pi.program));
            gc.check('2', ()=>this.initializeFinishProgram(gc, pi));

            // upload those into the accumulator textures
            gc.checkAPI('3', ()=>gl.bindTexture(gl.TEXTURE_2D, this.accLatentCTexture));
            gc.checkAPI('4', ()=>gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, this.width, this.height, gl.RGBA_INTEGER, gl.UNSIGNED_SHORT, buff0123));
            gc.checkAPI('5', ()=>gl.bindTexture(gl.TEXTURE_2D, this.accLatentMTexture));
            gc.checkAPI('6', ()=>gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, this.width, this.height, gl.RGBA_INTEGER, gl.UNSIGNED_SHORT, buff5678));
            gc.checkAPI('7', ()=>gl.bindTexture(gl.TEXTURE_2D, this.accDepthTexture));
            gc.checkAPI('8', ()=>gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, this.width, this.height, gl.RED_INTEGER, gl.UNSIGNED_SHORT, buffSelf));
            gc.check('9', ()=>this.useAccumulator(gc));

            // last output is the working
            this.stashOutput();
            gc.check('10', ()=>this.bindStashAsWorking(gc));

            // reference input is the wet paint
            gc.check('11', ()=>this.usePageReference(gc, this.initialWetLatentCTexture, this.initialWetLatentMTexture, this.initialWetDepthTexture));

            gc.check('12', ()=>this.useAvailableOutputFrame(gc));
            gc.checkFB('pre13');
            gc.checkAPI('13', ()=>gl.drawArrays(gl.TRIANGLES, 0, PageChange.verts));

            //this.debugSpewDepthTexture(this.outputDepthTexture!));
            //let d2 = this.debugMeasureDepthTexture(this.outputDepthTexture!, false)[0];
            //if (d2 !== d1) {
            //    console.log('different', d2!-d1!));
            //}

        }

        //  pull out the final wet colors and depths into the page texture
        const fbTemp1 = gc.checkAPI('14', ()=>gl.createFramebuffer(), true) as WebGLFramebuffer;
        gc.checkAPI('15', ()=>gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbTemp1));
        gc.checkAPI('16', ()=>gl.framebufferTexture2D(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.activeOutputLatentCTexture, 0));
        gc.checkAPI('17', ()=>gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.pageWetLatentCFramebufferW));
        gc.checkAPI('18', ()=>gl.blitFramebuffer(0, 0, this.width, this.height, 0, 0, this.width, this.height, gl.COLOR_BUFFER_BIT, gl.NEAREST));

        gc.checkAPI('19', ()=>gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbTemp1));
        gc.checkAPI('20', ()=>gl.framebufferTexture2D(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.activeOutputLatentMTexture, 0));
        gc.checkAPI('21', ()=>gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.pageWetLatentMFramebufferW));
        gc.checkAPI('22', ()=>gl.blitFramebuffer(0, 0, this.width, this.height, 0, 0, this.width, this.height, gl.COLOR_BUFFER_BIT, gl.NEAREST));

        gc.checkAPI('23', ()=>gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbTemp1));
        gc.checkAPI('24', ()=>gl.framebufferTexture2D(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.activeOutputDepthTexture, 0));
        gc.checkAPI('25', ()=>gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.pageWetDepthFramebufferW));
        gc.checkAPI('26', ()=>gl.blitFramebuffer(0, 0, this.width, this.height, 0, 0, this.width, this.height, gl.COLOR_BUFFER_BIT, gl.NEAREST));

    }

    override selectFinishProgram(gcFrom: GPUChecker): TouchProgramInfo {
        let gl = GPURunner.gl!;
        let pi = TouchPageChange.wetChangesToVisibleChangesProgram;

        gcFrom.checkAPI('touch-sfp', ()=>gl.useProgram(pi.program));
        return pi;
    
    }
    override initializeFinishProgram(gcFrom: GPUChecker, pi: TouchProgramInfo): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'touch-pass');
        let buffers = TouchPageChange.touchBuffers!;
        this._initializeFinishProgram(gc, pi, buffers);

    }
    _initializeFinishProgram(gcFrom: GPUChecker, pi: TouchProgramInfo, buffers: TouchProgramBuffers): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, '_initializeFinishProgram');
        const gl = gc.gl;

        gc.check('1', ()=>this._initializeSharedProgram(gc, pi, buffers));
        if (GPURunner.doInitializeProgram(pi, 'touch')) {
            gc.checkAPI('2', ()=>gl.uniform1i(pi.uniformLocations.workingWetAgeTexNum, TouchPageChange.workingWetAgeTexNumVal));
        }
    }

    override specializeFinishRoutine(gcFrom: GPUChecker, pi: TouchProgramInfo): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'touch-pass');
        let buffers = TouchPageChange.touchBuffers!;
        this._specializeFinishRoutine(gc, pi, buffers);
    }

    _specializeFinishRoutine(gcFrom: GPUChecker, pi: TouchProgramInfo, buffers: TouchProgramBuffers): void {
    }

    _initializeOutputProgram(gcFrom: GPUChecker, pi: TouchProgramInfo, buffers: TouchProgramBuffers) {
        let gl = GPURunner.gl!;
        if (GPURunner.doInitializeProgram(pi, 'touch')) {
            gl.uniform1i(pi.uniformLocations.kmLookupTexNum, TouchPageChange.kmLookupTexNumVal);
            gl.uniform1i(pi.uniformLocations.workingLatentCTexNum, TouchPageChange.workingLatentCTexNumVal);
            gl.uniform1i(pi.uniformLocations.workingLatentMTexNum, TouchPageChange.workingLatentMTexNumVal);
            gl.uniform1i(pi.uniformLocations.workingWetDepthTexNum, TouchPageChange.workingWetDepthTexNumVal);
            gl.uniform1i(pi.uniformLocations.workingDryDepthTexNum, TouchPageChange.workingDryDepthTexNumVal);
        }

    }

    override runFinishRoutine(gcFrom: GPUChecker, pi: TouchProgramInfo): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'touch-pass');
        let buffers = TouchPageChange.touchBuffers!;
        this._runFinishRoutine(gc, pi, buffers);
    }

    _runFinishRoutine(gcFrom: GPUChecker, pi: TouchProgramInfo, buffers: TouchProgramBuffers): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, '_runFinishRoutine');
        const gl = gc.gl;


        // use the finish program to make a set of combinations and extractions
        // find a mask (5) of the wet paint that is different from the starting point in color or depth
        // the dry source colors are mixed (6) with the wet paint mask to make a new visible color mask
        // the old visible is compared with the new visible mask to find places that need antialiasing (7) and to produce
        // the final visible color canvas
    
        // move the last result to the wet paint texture
        //debugRepeatDraw();

        //this.debugRenderLatentOutput(pi);
        //this.debugRenderLatentOutput(pi, true);
        // make sure the last output frame is the source
        
        //console.log('fun finish outputDepthTexture');
        //this.debugSpewDepthTexture(this.outputDepthTexture!);

        
        // let preLats = this.debugMeasureOutput();
        // for (let i = 0; i < 1; i++) {
        //     this.stashOutput();
        //     gc.check('d1', ()=>this.bindStashAsWorking(gc));
        //     pi = TouchPageChange.debugTypeEffects;
        //     gc.checkAPI('d2', ()=>gl.useProgram(pi.program));
        //     gc.check('d3', ()=>this.initializeFinishProgram(gc, pi));
        //     gc.check('d4', ()=>this.useAvailableOutputFrame(gc));
        //     gc.checkAPI('d5', ()=>gl.drawArrays(gl.TRIANGLES, 0, PageChange.verts));
        // }
        // let postLats = this.debugMeasureOutput();
        // if (preLats[1] != postLats[1] || preLats[2] != postLats[2] || preLats[3] != postLats[3] || preLats[4] != postLats[4] || 
        //     preLats[5] != postLats[5] || preLats[6] != postLats[6] || preLats[7] != postLats[7] || preLats[8] != postLats[8] || preLats[9] != postLats[9]) {
        //         console.log(`abs cnt:${postLats[1]} d:${postLats[2]} cB:${postLats[3]} cY:${postLats[4]} cR:${postLats[5]} cW:${postLats[6]} mr:${postLats[7]} mg:${postLats[8]} mb:${postLats[9]}`);
        //         console.log(`avg cnt:${postLats[1]} d:${postLats[2]} cB:${Math.floor(postLats[3]/postLats[2])} cY:${Math.floor(postLats[4]/postLats[2])} cR:${Math.floor(postLats[5]/postLats[2])} cW:${Math.floor(postLats[6]/postLats[2])} mr:${Math.floor(postLats[7]/postLats[2])} mg:${Math.floor(postLats[8]/postLats[2])} mb:${Math.floor(postLats[9]/postLats[2])}`);
        //         console.log(`dav cnt:${postLats[1]-preLats[1]} d${Math.floor(postLats[2]-preLats[2])} cB${Math.floor(postLats[3]/postLats[2]-preLats[3]/preLats[2])} cY${Math.floor(postLats[4]/postLats[2]-preLats[4]/preLats[2])} cR${Math.floor(postLats[5]/postLats[2]-preLats[5]/preLats[2])} cW${Math.floor(postLats[6]/postLats[2]-preLats[6]/preLats[2])} mr${Math.floor(postLats[7]/postLats[2]-preLats[7]/preLats[2])} mg${Math.floor(postLats[8]/postLats[2]-preLats[8]/preLats[2])} mb${Math.floor(postLats[9]/postLats[2]-preLats[9]/preLats[2])}`);
        // } else {
        //     console.log('no change');
        // }

        //this.debugSpewLatentTexture(buffers.initialWetLatentCTexture!);


        this.stashOutput();
        gc.check('1', ()=>this.bindStashAsWorking(gc));
                //this.debugSpewLatentTexture(buffers.initialWetLatentCTexture!);

        //this.debugSpewDepthTexture(this.pageWetDepthTexture!);

        // make the new wet paint mask by looking for differences between the wet paint and the starting point
        // the new wet paint is already set as the source, so make the starting wet the accumulator
        // use that to make the new visible mask by blending the dry source with the wet paint mask
        // external source is the dry source
        // put dry source into the latent form

        pi = TouchPageChange.wetChangesToVisibleChangesProgram;
        gc.checkAPI('2', ()=>gl.useProgram(pi.program));
        gc.check('3', ()=>this.initializeFinishProgram(gc, pi));
        // make a special output frame with the accumulator and the page age texture
        const fbWAge = gc.checkAPI('4', ()=>GPURunner.initTextureFramebuffer(gc, 'fbWAge', this.accLatentCTexture!, this.accLatentMTexture!, this.accDepthTexture!, this.pageWetAgeTexture!), true) as WebGLTexture;;
        gc.check('5', ()=>this.bindOutputFrame(gc, fbWAge, this.accLatentCTexture, this.accLatentMTexture, this.accDepthTexture));
        // reference input is the wet paint
        gc.check('6', ()=>this.usePageReference(gc, this.pageDryLatentCTexture, this.pageDryLatentMTexture, null));
        // initial wet paint is the accumulator
        gc.check('7', ()=>this.useTempAccumulator(gc, null, this.initialWetLatentCTexture, this.initialWetLatentMTexture, this.initialWetDepthTexture));
        //this.debugSpewLatentTexture(this.initialWetLatentCTexture!);

        // special case for the age texture
        gc.check('8', ()=>GPURunner.activateTexture(gc, TouchPageChange.workingWetAgeTexNumVal, this.initialWetAgeTexture));
        gc.checkAPI('10', ()=>gl.uniform1i(pi.uniformLocations.dryTransmits, this.dryTransmits === true ? 1 : 0));
        gc.checkFB('pre11');
        gc.checkAPI('11', ()=>gl.drawArrays(gl.TRIANGLES, 0, PageChange.verts));
        //this.debugRenderLatentOutput(pi);

        // antialias and make final visible. source is the old visible canvas
        // 
        pi = TouchPageChange.visibleChangesToFinalProgram;
        gc.checkAPI('12', ()=>gl.useProgram(pi.program));
        gc.check('13', ()=>this.initializeFinishProgram(gc, pi));
        //gl.uniform1i(pi.uniformLocations.doAntiAlias, this.centralPull === undefined ? 1 : 0);
        //gl.uniform1i(pi.uniformLocations.doAntiAlias, this.centralPull === undefined ? 1 : 0);


        this.stashOutput();
        gc.check('14', ()=>this.useTempAccumulator(gc, this.activeStashedFramebuffer, this.activeStashedLatentCTexture, this.activeStashedLatentMTexture, null));
        gc.check('15', ()=>this.bindWorkingFrame(gc, null, this.pageVisibleLatentCTexture, this.pageVisibleLatentMTexture, null));

        gc.check('16', ()=>this.useAvailableOutputFrame(gc));
        gc.checkAPI('17', ()=>gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1]));
        gc.checkFB('pre18');
        gc.checkAPI('18', ()=>gl.drawArrays(gl.TRIANGLES, 0, PageChange.verts));
        gc.checkAPI('19', ()=>gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1, gl.COLOR_ATTACHMENT2]));

        //this.debugRenderLatentOutput(pi, true);


        //this.debugSpewTexture(this.outputLatentCTexture!, true);
        //this.debugRenderLatentOutput(pi);

        // put the new visible into the visible color texture
        const fbTemp1 = gc.checkAPI('20', ()=>gl.createFramebuffer(), true) as WebGLFramebuffer;
        gc.checkAPI('21', ()=>gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbTemp1));
        gc.checkAPI('22', ()=>gl.framebufferTexture2D(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.activeOutputLatentCTexture, 0));
        gc.checkAPI('23', ()=>gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.pageVisibleLatentCFramebufferW));
        gc.checkAPI('24', ()=>gl.blitFramebuffer(0, 0, this.width, this.height, 0, 0, this.width, this.height, gl.COLOR_BUFFER_BIT, gl.NEAREST));

        gc.checkAPI('25', ()=>gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbTemp1));
        gc.checkAPI('26', ()=>gl.framebufferTexture2D(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.activeOutputLatentMTexture, 0));
        gc.checkAPI('27', ()=>gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.pageVisibleLatentMFramebufferW));
        gc.checkAPI('28', ()=>gl.blitFramebuffer(0, 0, this.width, this.height, 0, 0, this.width, this.height, gl.COLOR_BUFFER_BIT, gl.NEAREST));

        gl.deleteFramebuffer(fbTemp1);
        gl.deleteFramebuffer(fbWAge);
        
    }
    override finalizeFinish(gcFrom: GPUChecker, pi: TouchProgramInfo): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'touch-pass');
        let buffers = TouchPageChange.touchBuffers!;
        this._finalizeFinish(gc, pi, buffers);

    }
    _finalizeFinish(gcFrom: GPUChecker, pi: TouchProgramInfo, buffers: TouchProgramBuffers): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, '_finalizeFinish');
        const gl = gc.gl;

        //this.debugRepeatDraw();

        pi = TouchPageChange.latentToExternalProgram;
        gc.checkAPI('1', ()=>gl.useProgram(pi.program));
        gc.check('2', ()=>this._initializeOutputProgram(gc, pi, buffers));
        gc.checkAPI('3', ()=>gl.uniform1i(pi.uniformLocations.doDepthLighting, 1));

        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gc.check('4', ()=>this.bindWorkingFrame(gc, null, this.pageVisibleLatentCTexture, this.pageVisibleLatentMTexture, null));
        gc.check('5', ()=>GPURunner.activateTexture(gc, TouchPageChange.workingDryDepthTexNumVal, this.pageDryDepthTexture));

        gc.checkFB('pre7');
        gc.checkAPI('7', ()=>gl.drawArrays(gl.TRIANGLES, 0, PageChange.verts));
        //this.debugRenderLatentOutput(pi);

        this.page.crcVisible.drawImage(gl.canvas, this.pageX, this.pageY);
    }

}


